import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersApi, factoriesApi, customersApi, chainsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Package, Factory, Calendar, Clock, AlertTriangle, 
  CheckCircle2, Gem, Link2, Truck, Edit2, Plus, History, Loader2,
  AlertCircle, Save, X, PackageCheck, ArrowRight, MoveRight, Printer, Send, Copy, IndianRupee, Pencil, Trash2, ChevronDown, FileText, Boxes
} from 'lucide-react';
import { formatDate, formatDateTime, getDaysUntil, isOrderDispatchedOrClosed, getPriorityClass, getProgressPercentage, cn } from '../lib/utils';
import EditDispatchDialog from '../components/EditDispatchDialog';
import BillToPicker from '../components/BillToPicker';
import WhatsAppRecipientPicker from '../components/WhatsAppRecipientPicker';
import FactoryDeadlineDialog from '../components/FactoryDeadlineDialog';
import ChainBreakdownPicker from '../components/ChainBreakdownPicker';
import DesignDependenciesCard from '../components/DesignDependenciesCard';
import ManufacturingFormCard from '../components/ManufacturingFormCard';
import FactoryProcessingSheetCard from '../components/FactoryProcessingSheetCard';

const PRODUCTION_STAGES = [
  "Order Raised",
  "From Inventory",
  "Casting",
  "On the Floor",
  "Received from Factory",
  "Hallmark / Stamping",
  "Certification",
  "Ready to Ship",
  "Dispatched"
];

const DIAMOND_QUALITIES = ["VVS EF", "GH SI", "VSSI GH", "SI-IJ", "VVSVS GH", "VVS FG"];
const GOLD_KARATS = ["14kt", "18kt", "22kt"];
const CERTIFICATION_LABS = ["IGI", "SGL", "EGL", "Other"];

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isProduction, user } = useAuth();
  const [order, setOrder] = useState(null);
  const [designState, setDesignState] = useState(null);
  const [factoryDeadlineOpen, setFactoryDeadlineOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [factories, setFactories] = useState([]);
  const [chainSkus, setChainSkus] = useState([]);  // Chain inventory snapshot for stock display in Materials tab
  // Edit Dispatch dialog state (super_admin + admin only)
  const [editingDispatch, setEditingDispatch] = useState(null);
  
  // Edit order state
  const [editForm, setEditForm] = useState({});
  // Typed-confirmation state for sensitive edits
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Stage editing state
  const [isEditingStages, setIsEditingStages] = useState(false);
  const [editedStageQuantities, setEditedStageQuantities] = useState({});
  
  // Movement tracking dialogs
  const [isMovementDialogOpen, setIsMovementDialogOpen] = useState(false);
  const [isIssuanceDialogOpen, setIsIssuanceDialogOpen] = useState(false);
  const [movementData, setMovementData] = useState({ from_stage: '', to_stage: '', pieces: '', notes: '' });
  const [issuanceData, setIssuanceData] = useState({ material_type: 'diamonds', quantity: '', pieces_covered: '', notes: '' });
  const [stickerDialogOpen, setStickerDialogOpen] = useState(false);
  const [goldRateCutDialogOpen, setGoldRateCutDialogOpen] = useState(false);
  const [rateCutForm, setRateCutForm] = useState({ rate_type: '995_gst', rate_type_other: '', gold_rate_per_10g: '', notes: '' });
  const [rateCutFile, setRateCutFile] = useState(null);
  const [savingRateCut, setSavingRateCut] = useState(false);
  const [requestChatId, setRequestChatId] = useState(null);
  const [requestChatMsg, setRequestChatMsg] = useState('');
  
  // Edit issuance state
  const [editIssuance, setEditIssuance] = useState(null); // { material_type, ...issuance }
  const [issuanceForm, setIssuanceForm] = useState({ quantity: '', pieces_covered: '', notes: '' });
  const [savingIssuance, setSavingIssuance] = useState(false);
  
  // Pending receipts (factory dispatch → admin signature)
  const [receiptAction, setReceiptAction] = useState(null); // { kind: 'accept'|'reject', receipt }
  const [receiptForm, setReceiptForm] = useState({ signature: '', notes: '' });
  const [savingReceipt, setSavingReceipt] = useState(false);
  
  // Dispatch & Invoice state
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [moveToStockDialogOpen, setMoveToStockDialogOpen] = useState(false);
  const [moveToStockPieces, setMoveToStockPieces] = useState(0);
  const [moveToStockNotes, setMoveToStockNotes] = useState('');
  const [movingToStock, setMovingToStock] = useState(false);
  const [dispatchPieces, setDispatchPieces] = useState(0);
  const [dispatchStep, setDispatchStep] = useState('choose'); // 'choose' | 'approval' | 'invoice'
  const [pendingMovement, setPendingMovement] = useState(null); // stores movement data until dispatch form is filled
  const [dispatchForm, setDispatchForm] = useState({
    memo_number: '', invoice_number: '', total_amount: '', logistics: '', tracking_number: '', notes: '',
    gold_weight: '', diamond_weight: '',
    bill_to_customer_id: '', bill_to_customer_name: '',
  });
  const [invoiceMemoDialog, setInvoiceMemoDialog] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '', total_amount: '', logistics: '', tracking_number: '',
    gold_weight: '', diamond_weight: '',
    bill_to_customer_id: '', bill_to_customer_name: '',
  });
  // Bill-To popover open states + shared approved-customers preload for the
  // dispatch/memo dialogs (independent of the Edit Order popover).
  const [billToDispatchOpen, setBillToDispatchOpen] = useState(false);
  const [billToMemoOpen, setBillToMemoOpen] = useState(false);

  // WhatsApp manual send
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [sendingWa, setSendingWa] = useState(false);
  const [waSelectedPhones, setWaSelectedPhones] = useState([]);

  // Approved customer picker (for Edit Order)
  const [approvedCustomers, setApprovedCustomers] = useState([]);
  const [customerComboOpen, setCustomerComboOpen] = useState(false);
  const [loadingApprovedCustomers, setLoadingApprovedCustomers] = useState(false);

  const [issuingChains, setIssuingChains] = useState(false);
  const [issueChainsConfirmOpen, setIssueChainsConfirmOpen] = useState(false);

  // One-click issue all chains from breakdown — deducts SKU stock + logs issuance
  const handleIssueChainsFromBreakdown = async () => {
    setIssuingChains(true);
    try {
      await ordersApi.issueChainsFromBreakdown(id);
      toast.success('Chains issued from inventory — stock deducted and issuance logged');
      setIssueChainsConfirmOpen(false);
      // Refresh order + chain stock snapshot
      await fetchOrder();
      try {
        const r = await chainsApi.getAll();
        setChainSkus(Array.isArray(r.data) ? r.data : []);
      } catch (_) { /* non-fatal */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue chains from breakdown');
    } finally {
      setIssuingChains(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    fetchFactories();
    // Snapshot chain inventory so the Materials tab can display live stock against
    // the chain_breakdown saved on the order
    chainsApi.getAll()
      .then(r => setChainSkus(Array.isArray(r.data) ? r.data : []))
      .catch(() => setChainSkus([]));
  }, [id]);

  const fetchFactories = async () => {
    try {
      const response = await factoriesApi.getAll();
      setFactories(response.data);
    } catch (error) {
      console.error('Failed to load factories');
    }
  };
  const handleMoveToStock = async () => {
    if (!moveToStockPieces || parseInt(moveToStockPieces) <= 0) {
      toast.error('Enter pieces > 0');
      return;
    }
    setMovingToStock(true);
    try {
      await ordersApi.moveToStock(id, { pieces: parseInt(moveToStockPieces), notes: moveToStockNotes });
      toast.success('Moved to stock and order closed');
      setMoveToStockDialogOpen(false);
      setMoveToStockPieces(0);
      setMoveToStockNotes('');
      fetchOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to move to stock');
    } finally {
      setMovingToStock(false);
    }
  };


  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await ordersApi.getById(id);
      setOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  // Initialize edited quantities when entering edit mode
  const handleStartEditingStages = () => {
    const quantities = {};
    order.stage_quantities?.forEach(sq => {
      quantities[sq.stage] = sq.quantity;
    });
    setEditedStageQuantities(quantities);
    setIsEditingStages(true);
  };

  // Cancel editing
  const handleCancelEditingStages = () => {
    setIsEditingStages(false);
    setEditedStageQuantities({});
  };

  // Update a single stage quantity in edit mode
  const handleStageQuantityChange = (stage, value) => {
    const numValue = parseInt(value) || 0;
    setEditedStageQuantities(prev => ({
      ...prev,
      [stage]: Math.max(0, numValue)
    }));
  };

  // Calculate totals for validation
  const editedTotal = useMemo(() => {
    return Object.values(editedStageQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
  }, [editedStageQuantities]);

  const tallyDifference = useMemo(() => {
    if (!order) return 0;
    return order.pieces_ordered - editedTotal;
  }, [order, editedTotal]);

  const isTallyValid = tallyDifference === 0;

  // Save all stage quantities
  const handleSaveStageQuantities = async () => {
    if (!isTallyValid) {
      toast.error(`Total pieces (${editedTotal}) must equal order total (${order.pieces_ordered})`);
      return;
    }

    setUpdating(true);
    try {
      await ordersApi.bulkUpdateStages(id, {
        stage_quantities: editedStageQuantities
      });
      toast.success('Stage quantities updated successfully');
      setIsEditingStages(false);
      setEditedStageQuantities({});
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stages');
    } finally {
      setUpdating(false);
    }
  };

  // Mark order as dispatched (move all pieces to Dispatched stage)
  const handleMarkAsDispatched = async () => {
    if (order.status === 'Dispatched') {
      toast.info('Order is already dispatched');
      return;
    }

    // Store the pending movement — don't execute yet
    setPendingMovement({
      type: 'mark_dispatched',
      pieces: order.pieces_ordered,
    });
    setDispatchPieces(order.pieces_ordered);
    setDispatchStep('choose');
    setDispatchForm({ memo_number: '', invoice_number: '', total_amount: '', logistics: '', tracking_number: '', notes: '',
      gold_weight: parseFloat(order.total_gold_weight || 0).toFixed(2),
      diamond_weight: parseFloat(order.total_diamond_weight || 0).toFixed(2),
      bill_to_customer_id: '', bill_to_customer_name: '',
    });
    setDispatchDialogOpen(true);
  };

  // Open edit dialog with current order data
  const handleOpenEditDialog = () => {
    setEditForm({
      customer_id: order.customer_id || '',
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      branch: order.branch || '',
      salesperson: order.salesperson || '',
      factory_id: order.factory_id || '',
      co_number: order.co_number || '',
      pieces_ordered: order.pieces_ordered || 0,
      ready_from_inventory: order.ready_from_inventory || 0,
      diamond_quality: order.diamond_quality || '',
      gold_karat: order.gold_karat || '18kt',
      certification_lab: order.certification_lab || '',
      certification_lab_other: order.certification_lab_other || '',
      cobranding: order.cobranding || false,
      inventory_gold_weight: order.inventory_gold_weight || 0,
      inventory_diamond_weight: order.inventory_diamond_weight || 0,
      order_gold_weight: order.order_gold_weight || 0,
      order_diamond_weight: order.order_diamond_weight || 0,
      chains_pieces_inventory: order.chains_pieces_inventory || 0,
      chains_pieces_factory: order.chains_pieces_factory || 0,
      chain_breakdown: (order.materials?.chains?.breakdown || []).map(b => ({ ...b, quantity: String(b.quantity || '') })),
      delivery_date: order.delivery_date ? new Date(order.delivery_date).toISOString().split('T')[0] : '',
      priority: order.priority || 'Normal',
      status: order.status || 'Order Received',
      notes: order.notes || '',
      is_stock_order: order.is_stock_order || false,
      // Pre-seed from the current order so the picker reflects existing selections
      notification_phones: order.notification_phones || [],
    });
    setIsEditDialogOpen(true);
    // Load approved customers list for the dropdown (lazy, once)
    if (approvedCustomers.length === 0 && !loadingApprovedCustomers) {
      setLoadingApprovedCustomers(true);
      customersApi.getApprovedForOrder()
        .then(res => setApprovedCustomers(res.data || []))
        .catch(() => toast.error('Could not load approved customers'))
        .finally(() => setLoadingApprovedCustomers(false));
    }
  };

  const handleSelectCustomerInEdit = (cust) => {
    if (!cust) return;
    setEditForm(prev => ({
      ...prev,
      customer_id: cust.id,
      customer_name: cust.company_name || '',
      // Only auto-fill phone if it was empty before; don't overwrite a manually edited one silently
      customer_phone: prev.customer_phone || cust.mobile || '',
    }));
    setCustomerComboOpen(false);
  };

  // Compute diff list (label, before, after, warning?) between editForm and current order
  const FIELD_LABELS = {
    customer_name: 'Customer Name',
    customer_phone: 'Customer WhatsApp',
    branch: 'Branch',
    salesperson: 'Salesperson',
    factory_id: 'Factory',
    co_number: 'CO Number',
    pieces_ordered: 'Pieces Ordered',
    ready_from_inventory: 'Ready From Inventory',
    diamond_quality: 'Diamond Quality',
    gold_karat: 'Gold Karat',
    certification_lab: 'Certification Lab',
    certification_lab_other: 'Other Lab Name',
    cobranding: 'Co-branding',
    inventory_gold_weight: 'Inventory Gold (g)',
    inventory_diamond_weight: 'Inventory Diamond (ct)',
    order_gold_weight: 'Order Gold (g)',
    order_diamond_weight: 'Order Diamond (ct)',
    chains_pieces_inventory: 'Chain Pieces (Inventory)',
    chains_pieces_factory: 'Chain Pieces (Factory)',
    chain_breakdown: 'Chain SKU Breakdown',
    delivery_date: 'Delivery Date',
    priority: 'Priority',
    status: 'Status',
    notes: 'Notes',
  };
  const SENSITIVE_KEYS = ['pieces_ordered', 'ready_from_inventory', 'gold_karat', 'co_number', 'factory_id', 'order_gold_weight', 'order_diamond_weight', 'chains_pieces_factory', 'status'];

  const computeDiff = () => {
    if (!order) return [];
    const diffs = [];
    // Helper — flatten the chain_breakdown array to a short comparable string
    const breakdownToStr = (arr) => {
      const rows = (arr || [])
        .filter(b => b && b.chain_sku_id && parseInt(b.quantity || 0) > 0)
        .map(b => `${(b.chain_label || b.chain_sku_id || '').trim()}×${parseInt(b.quantity || 0)}`)
        .sort();
      return rows.join(', ') || '—';
    };
    Object.keys(FIELD_LABELS).forEach(k => {
      let before = order[k];
      let after = editForm[k];
      // Normalize delivery_date
      if (k === 'delivery_date') {
        before = before ? new Date(before).toISOString().split('T')[0] : '';
        after = after || '';
      }
      // Factory name lookup for display
      if (k === 'factory_id') {
        const fBefore = factories.find(f => f.id === before);
        const fAfter = factories.find(f => f.id === after);
        before = fBefore?.name || before || '—';
        after = fAfter?.name || after || '—';
      }
      // Chain breakdown — sourced from materials.chains.breakdown, displayed as string
      if (k === 'chain_breakdown') {
        before = breakdownToStr(order.materials?.chains?.breakdown);
        after = breakdownToStr(editForm.chain_breakdown);
      }
      if (String(before ?? '') !== String(after ?? '')) {
        diffs.push({ key: k, label: FIELD_LABELS[k], before, after, sensitive: SENSITIVE_KEYS.includes(k) });
      }
    });
    return diffs;
  };

  // Save edited order — opens confirmation if changes exist
  const handleSaveOrder = () => {
    const diffs = computeDiff();
    if (diffs.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setConfirmText('');
    setConfirmEditOpen(true);
  };

  // Final confirmed save — runs after user types CHANGES
  const handleConfirmedSave = async () => {
    setUpdating(true);
    try {
      await ordersApi.update(id, editForm);
      toast.success('Order updated — all downstream fields synced');
      setConfirmEditOpen(false);
      setIsEditDialogOpen(false);
      setConfirmText('');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setUpdating(false);
    }
  };

  // Log movement of pieces between stages
  const handleLogMovement = async () => {
    if (!movementData.from_stage || !movementData.to_stage || !movementData.pieces) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (parseInt(movementData.pieces) <= 0) {
      toast.error('Pieces must be greater than 0');
      return;
    }

    setUpdating(true);
    try {
      // If moving to Dispatched, show dispatch dialog FIRST — don't execute movement yet
      if (movementData.to_stage === 'Dispatched') {
        setPendingMovement({
          type: 'movement',
          from_stage: movementData.from_stage,
          to_stage: movementData.to_stage,
          pieces: parseInt(movementData.pieces),
          notes: movementData.notes || '',
        });
        setDispatchPieces(parseInt(movementData.pieces));
        setDispatchStep('choose');
        setDispatchForm({ memo_number: '', invoice_number: '', total_amount: '', logistics: '', tracking_number: '', notes: '',
          gold_weight: parseFloat(order?.total_gold_weight || 0).toFixed(2),
          diamond_weight: parseFloat(order?.total_diamond_weight || 0).toFixed(2),
          bill_to_customer_id: '', bill_to_customer_name: '',
        });
        setIsMovementDialogOpen(false);
        setDispatchDialogOpen(true);
        setUpdating(false);
        return;
      }

      await ordersApi.logMovement(id, {
        from_stage: movementData.from_stage,
        to_stage: movementData.to_stage,
        pieces: parseInt(movementData.pieces),
        notes: movementData.notes || ''
      });
      toast.success(`Moved ${movementData.pieces} pieces to ${movementData.to_stage}`);
      setIsMovementDialogOpen(false);
      
      setMovementData({ from_stage: '', to_stage: '', pieces: '', notes: '' });
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log movement');
    } finally {
      setUpdating(false);
    }
  };

  // Log material issuance
  const handleLogIssuance = async () => {
    if (!issuanceData.material_type || !issuanceData.quantity || !issuanceData.pieces_covered) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (parseFloat(issuanceData.quantity) <= 0 || parseInt(issuanceData.pieces_covered) <= 0) {
      toast.error('Quantity and pieces must be greater than 0');
      return;
    }

    setUpdating(true);
    try {
      await ordersApi.logMaterialIssuance(id, {
        material_type: issuanceData.material_type,
        quantity: parseFloat(issuanceData.quantity),
        pieces_covered: parseInt(issuanceData.pieces_covered),
        notes: issuanceData.notes || ''
      });
      const unit = issuanceData.material_type === 'gold' ? 'g' : (issuanceData.material_type === 'diamonds' ? 'ct' : 'pcs');
      toast.success(`Issued ${issuanceData.quantity}${unit} ${issuanceData.material_type} for ${issuanceData.pieces_covered} pieces`);
      setIsIssuanceDialogOpen(false);
      setIssuanceData({ material_type: 'diamonds', quantity: '', pieces_covered: '', notes: '' });
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log issuance');
    } finally {
      setUpdating(false);
    }
  };

  // Open edit dialog for an existing issuance entry
  const openEditIssuance = (materialType, issuance) => {
    setEditIssuance({ material_type: materialType, ...issuance });
    setIssuanceForm({
      quantity: issuance.quantity ?? '',
      pieces_covered: issuance.pieces_covered ?? '',
      notes: issuance.notes || '',
    });
  };

  const handleSaveEditIssuance = async () => {
    if (!editIssuance) return;
    if (issuanceForm.quantity === '' || issuanceForm.pieces_covered === '') {
      toast.error('Quantity and pieces are required');
      return;
    }
    setSavingIssuance(true);
    try {
      await ordersApi.updateMaterialIssuance(id, editIssuance.id, editIssuance.material_type, {
        quantity: parseFloat(issuanceForm.quantity),
        pieces_covered: parseInt(issuanceForm.pieces_covered),
        notes: issuanceForm.notes || '',
      });
      toast.success('Issuance updated — totals recomputed');
      setEditIssuance(null);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update issuance');
    } finally {
      setSavingIssuance(false);
    }
  };

  const handleDeleteIssuance = async (materialType, issuance) => {
    const unit = materialType === 'gold' ? 'g' : (materialType === 'diamonds' ? 'ct' : 'pcs');
    if (!window.confirm(`Delete this ${materialType} issuance of ${issuance.quantity}${unit} for ${issuance.pieces_covered} pcs? Totals will be recomputed.`)) return;
    try {
      await ordersApi.deleteMaterialIssuance(id, issuance.id, materialType);
      toast.success('Issuance deleted');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete issuance');
    }
  };

  // Pending receipt handlers
  const openReceiptAction = (kind, receipt) => {
    setReceiptAction({ kind, receipt });
    setReceiptForm({ signature: '', notes: '' });
  };
  const handleSubmitReceipt = async () => {
    if (!receiptAction) return;
    if (!receiptForm.signature || !receiptForm.signature.trim()) {
      toast.error('Please type your signature to confirm');
      return;
    }
    setSavingReceipt(true);
    try {
      const fn = receiptAction.kind === 'accept' ? ordersApi.acceptReceipt : ordersApi.rejectReceipt;
      await fn(id, receiptAction.receipt.id, {
        signature: receiptForm.signature.trim(),
        notes: receiptForm.notes || '',
      });
      toast.success(receiptAction.kind === 'accept' ? 'Receipt signed — pieces in Received from Factory' : 'Rejected — pieces returned to factory Finished Goods');
      setReceiptAction(null);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setSavingReceipt(false);
    }
  };

  // Sticker print confirmation
  const handleStickerConfirm = async () => {
    setUpdating(true);
    try {
      await ordersApi.updateStickerStatus(id, { sticker_printed: true });
      toast.success('Sticker marked as printed');
      setStickerDialogOpen(false);
      fetchOrder();
    } catch (error) {
      toast.error('Failed to update sticker status');
    } finally {
      setUpdating(false);
    }
  };

  // PayForward — download the early-payment supporting PDF for an invoiced dispatch
  const downloadPayForward = async (dispatch) => {
    try {
      const r = await ordersApi.downloadPayForward(id, dispatch.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safe = (dispatch.invoice_number || dispatch.id.slice(0, 8)).replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `PayForward_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PayForward PDF downloaded');
    } catch (e) {
      // Blob errors don't expose JSON detail — read the blob if present
      let msg = 'Failed to download PayForward PDF';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch { /* noop */ }
      toast.error(msg);
    }
  };

  // Gold Rate Cut
  const openGoldRateCutDialog = () => {
    if (order?.gold_rate_cut?.rate_type && user?.role !== 'super_admin') {
      toast.error('Only Super Admin can edit an existing gold rate cut');
      return;
    }
    if (order?.gold_rate_cut?.rate_type) {
      setRateCutForm({
        rate_type: order.gold_rate_cut.rate_type,
        rate_type_other: order.gold_rate_cut.rate_type_other || '',
        gold_rate_per_10g: String(order.gold_rate_cut.gold_rate_per_10g),
        notes: order.gold_rate_cut.notes || '',
      });
    } else {
      setRateCutForm({ rate_type: '995_gst', rate_type_other: '', gold_rate_per_10g: '', notes: '' });
    }
    setRateCutFile(null);
    setGoldRateCutDialogOpen(true);
  };

  const handleSaveGoldRateCut = async () => {
    if (!rateCutForm.gold_rate_per_10g || parseFloat(rateCutForm.gold_rate_per_10g) <= 0) { toast.error('Enter a valid gold rate'); return; }
    if (rateCutForm.rate_type === 'other' && !rateCutForm.rate_type_other.trim()) { toast.error('Specify rate type'); return; }
    setSavingRateCut(true);
    try {
      await ordersApi.saveGoldRateCut(id, { ...rateCutForm, gold_rate_per_10g: parseFloat(rateCutForm.gold_rate_per_10g) });
      if (rateCutFile) {
        await ordersApi.uploadGoldRateScreenshot(id, rateCutFile);
      }
      toast.success('Gold rate cut saved');
      setGoldRateCutDialogOpen(false);
      fetchOrder();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save gold rate cut';
      toast.error(msg);
    }
    finally { setSavingRateCut(false); }
  };

  const handleCopyStatus = () => {
    if (!order) return;
    const stages = (order.stage_quantities || []).filter(s => s.quantity > 0);
    const stageText = stages.map(s => `  ${s.stage}: ${s.quantity} pcs`).join('\n');
    const deliveryDate = order.delivery_date ? formatDate(order.delivery_date) : 'Not set';
    const daysInfo = order.delivery_date && !isOrderDispatchedOrClosed(order) ? (() => {
      const days = getDaysUntil(order.delivery_date);
      if (days < 0) return ` (Overdue by ${Math.abs(days)} days)`;
      if (days === 0) return ' (Due today)';
      return ` (${days} days remaining)`;
    })() : '';

    const msg = [
      `*Siddhi Jewels — Order Update*`,
      ``,
      `Order: *${order.co_number || order.order_number}*`,
      `Customer: ${order.customer_name}`,
      `Total Pieces: ${order.pieces_ordered}`,
      `Status: *${order.status}*`,
      ``,
      `*Current Stage Breakdown:*`,
      stageText || '  No pieces in production',
      ``,
      `Estimated Delivery: *${deliveryDate}*${daysInfo}`,
      ``,
      `For any queries, please contact us.`,
      `— Siddhi Jewels`
    ].join('\n');

    navigator.clipboard.writeText(msg).then(() => {
      toast.success('Order status copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleSendWhatsApp = async () => {
    if (!waMessage.trim()) { toast.error('Please enter a message'); return; }
    if (!waSelectedPhones || waSelectedPhones.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    setSendingWa(true);
    try {
      const res = await ordersApi.sendWhatsApp(id, { message: waMessage.trim(), phones: waSelectedPhones });
      const sentCount = (res?.data?.sent_to || []).length;
      const failedCount = (res?.data?.failed || []).length;
      if (failedCount > 0) {
        toast.success(`Sent to ${sentCount} recipient${sentCount === 1 ? '' : 's'}; failed for ${failedCount}`);
      } else {
        toast.success(`WhatsApp sent to ${sentCount} recipient${sentCount === 1 ? '' : 's'}`);
      }
      setWaDialogOpen(false);
      setWaMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send WhatsApp');
    } finally { setSendingWa(false); }
  };

  const handleSubmitDispatch = async () => {
    const isApproval = dispatchStep === 'approval';
    if (isApproval && !dispatchForm.memo_number) {
      toast.error('Approval memo number is required');
      return;
    }
    if (!isApproval && !dispatchForm.invoice_number) {
      toast.error('Invoice number is required');
      return;
    }

    setUpdating(true);
    try {
      // Execute the pending movement first (pieces move to Dispatched)
      if (pendingMovement) {
        if (pendingMovement.type === 'mark_dispatched') {
          const dispatchedQuantities = {};
          PRODUCTION_STAGES.forEach(stage => {
            dispatchedQuantities[stage] = stage === 'Dispatched' ? order.pieces_ordered : 0;
          });
          await ordersApi.bulkUpdateStages(id, { stage_quantities: dispatchedQuantities });
        } else if (pendingMovement.type === 'movement') {
          await ordersApi.logMovement(id, {
            from_stage: pendingMovement.from_stage,
            to_stage: pendingMovement.to_stage,
            pieces: pendingMovement.pieces,
            notes: pendingMovement.notes || '',
          });
        }
        setPendingMovement(null);
        setMovementData({ from_stage: '', to_stage: '', pieces: '', notes: '' });
      }

      // Then create the dispatch record
      const payload = {
        dispatch_type: isApproval ? 'approval' : 'invoice',
        pieces: dispatchPieces,
        memo_number: dispatchForm.memo_number,
        invoice_number: dispatchForm.invoice_number,
        total_amount: parseFloat(dispatchForm.total_amount) || 0,
        logistics: dispatchForm.logistics,
        tracking_number: dispatchForm.tracking_number,
        notes: dispatchForm.notes,
      };
      if (!isApproval) {
        payload.gold_weight = parseFloat(dispatchForm.gold_weight) || 0;
        payload.gold_karat = order?.gold_karat || '18kt';
        payload.diamond_weight = parseFloat(dispatchForm.diamond_weight) || 0;
        // Bill-To override — only send when the user has picked a different customer.
        if (dispatchForm.bill_to_customer_id) {
          payload.bill_to_customer_id = dispatchForm.bill_to_customer_id;
          payload.bill_to_customer_name = dispatchForm.bill_to_customer_name || '';
        }
      }
      await ordersApi.createDispatch(id, payload);
      toast.success(isApproval ? 'Approval memo recorded' : 'Invoice recorded');
      setDispatchDialogOpen(false);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record dispatch');
    } finally {
      setUpdating(false);
    }
  };

  const handleConvertMemoToInvoice = async () => {
    if (!invoiceForm.invoice_number) {
      toast.error('Invoice number is required');
      return;
    }
    setUpdating(true);
    try {
      await ordersApi.convertMemoToInvoice(id, invoiceMemoDialog.id, {
        invoice_number: invoiceForm.invoice_number,
        total_amount: parseFloat(invoiceForm.total_amount) || 0,
        logistics: invoiceForm.logistics,
        tracking_number: invoiceForm.tracking_number,
        gold_weight: parseFloat(invoiceForm.gold_weight) || 0,
        gold_karat: order?.gold_karat || '18kt',
        diamond_weight: parseFloat(invoiceForm.diamond_weight) || 0,
        ...(invoiceForm.bill_to_customer_id
          ? { bill_to_customer_id: invoiceForm.bill_to_customer_id, bill_to_customer_name: invoiceForm.bill_to_customer_name || '' }
          : {}),
      });
      toast.success('Approval memo invoiced successfully');
      setInvoiceMemoDialog(null);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to invoice');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Order Details" />
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64 lg:col-span-2" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const progress = getProgressPercentage(order.ready_pieces, order.pieces_ordered);
  const daysUntil = getDaysUntil(order.delivery_date);
  const dispatched = isOrderDispatchedOrClosed(order);

  return (
    <div className="min-h-screen" data-testid="order-detail-page">
      <Header title="Order Details" onRefresh={fetchOrder}>
        <Button variant="ghost" onClick={() => navigate('/orders')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        {designState === 'waiting_on_design' && (
          <Badge className="ml-2 bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300 border-rose-300/60 hover:bg-rose-100" data-testid="design-state-badge">
            Waiting on Design
          </Badge>
        )}
        {designState === 'ready_for_production' && (
          <Badge className="ml-2 bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60 hover:bg-emerald-100" data-testid="design-state-badge">
            Ready for Production
          </Badge>
        )}
      </Header>

      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Factory acceptance banner — visible when the assigned factory hasn't
            yet confirmed receipt of the brief, or has rejected the order.
            Admin can re-send the order from here. */}
        {order.factory_acceptance && order.factory_acceptance.status !== 'accepted' && (
          (() => {
            const fa = order.factory_acceptance;
            const isPending = fa.status === 'pending';
            let waitText = '';
            if (fa.requested_at) {
              const hours = Math.max(0, Math.round((Date.now() - new Date(fa.requested_at).getTime()) / 3600000));
              waitText = hours < 1 ? 'just now'
                : hours < 24 ? `${hours}h ago`
                : `${Math.floor(hours / 24)}d ago`;
            }
            return (
              <Card className={isPending
                ? "border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40"
                : "border-red-300 bg-red-50 dark:bg-red-500/10 dark:border-red-500/40"}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isPending ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} />
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold ${isPending ? 'text-amber-900 dark:text-amber-200' : 'text-red-900 dark:text-red-200'}`}>
                          {isPending
                            ? `Awaiting ${order.factory_name || 'factory'} acceptance${waitText ? ` — sent ${waitText}` : ''}`
                            : `${order.factory_name || 'Factory'} rejected this order${waitText ? ` ${waitText}` : ''}`}
                        </p>
                        {!isPending && fa.reject_reason && (
                          <p className="text-[12px] text-red-700 dark:text-red-300 mt-1 italic whitespace-pre-wrap">
                            &ldquo;{fa.reject_reason}&rdquo;
                          </p>
                        )}
                        {isPending && (
                          <p className="text-[11px] text-amber-700 dark:text-amber-300">
                            Factory cannot log movements, raise requests, or change stages until they confirm receipt of the brief.
                          </p>
                        )}
                      </div>
                    </div>
                    {!isPending && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-500/40 dark:text-red-300"
                        data-testid="resend-to-factory-btn"
                        onClick={async () => {
                          if (!window.confirm('Re-send this order to the factory for acceptance? They will see it as a fresh pending order.')) return;
                          try {
                            await ordersApi.resetFactoryAcceptance(order.id);
                            toast.success('Order re-sent to factory');
                            fetchOrder();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Failed to re-send');
                          }
                        }}
                      >
                        Re-send to factory
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()
        )}

        {/* Header Info */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="h-12 w-12 md:h-14 md:w-14 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Package className="h-6 w-6 md:h-7 md:w-7 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 dark:text-white truncate">
                {order.order_number}
              </h2>
              <p className="text-sm md:text-base text-slate-500 truncate">{order.customer_name}{order.branch ? <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">— {order.branch}</span> : ''}{order.customer_phone ? <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">WA: {order.customer_phone}</span> : ''}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={getPriorityClass(order.priority)} variant="outline">
              {order.priority}
            </Badge>
            <Badge variant="outline" className="text-sm md:text-base px-3 md:px-4 py-1">
              {order.status}
            </Badge>
            {order.is_stock_order && (
              <Badge variant="outline" className="border-indigo-400 text-indigo-700 bg-indigo-50 dark:bg-indigo-500/15 dark:text-indigo-300 text-sm md:text-base px-3 md:px-4 py-1" data-testid="stock-order-badge">
                <Boxes className="h-3 w-3 mr-1" /> STOCK
              </Badge>
            )}
            {order.is_delayed && !dispatched && (
              <Badge variant="destructive" className="animate-pulse">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Delayed
              </Badge>
            )}
            {isProduction() && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenEditDialog}
                className="ml-auto"
                data-testid="edit-order-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Order
              </Button>
            )}
            {isProduction() && order.is_stock_order && !dispatched && (
              <Button
                size="sm"
                onClick={() => setMoveToStockDialogOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="mark-stocked-btn"
              >
                <Boxes className="h-4 w-4 mr-2" />
                Mark as Stocked & Close
              </Button>
            )}
            {/* Hide-from-factory-portal toggle — useful for cleaning up legacy
                orders the factory has already dispatched offline but that still
                appear in their portal due to stale stage-quantity data. */}
            {isProduction() && order.factory_id && (
              <Button
                variant="outline"
                size="sm"
                data-testid="dismiss-from-factory-btn"
                onClick={async () => {
                  const willHide = !order.factory_dismissed;
                  const msg = willHide
                    ? 'Hide this order from the factory portal? They will no longer see or be able to update it.'
                    : 'Re-enable this order on the factory portal?';
                  if (!window.confirm(msg)) return;
                  try {
                    await ordersApi.dismissFromFactory(order.id, willHide);
                    toast.success(willHide ? 'Hidden from factory portal' : 'Re-enabled on factory portal');
                    fetchOrder();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Action failed');
                  }
                }}
                className={order.factory_dismissed
                  ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-300'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300'}
              >
                <Factory className="h-4 w-4 mr-2" />
                {order.factory_dismissed ? 'Show on factory portal' : 'Hide from factory portal'}
              </Button>
            )}
            {isProduction() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setWaMessage('');
                  // Pre-populate with all recipients (primary + notification_phones), deduped
                  const recipients = [];
                  const seen = new Set();
                  const pushPhone = (ph) => {
                    if (!ph) return;
                    const norm = String(ph).trim();
                    if (norm && !seen.has(norm)) { seen.add(norm); recipients.push(norm); }
                  };
                  pushPhone(order?.customer_phone);
                  (order?.notification_phones || []).forEach(p => pushPhone(p?.phone));
                  setWaSelectedPhones(recipients);
                  setWaDialogOpen(true);
                }}
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-950"
                data-testid="whatsapp-update-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                WhatsApp Update
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Info & Progress */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Production Progress</CardTitle>
                    <CardDescription>
                      Total Order: {order.pieces_ordered} pieces | Ready: {order.ready_pieces} | Pending: {order.pending_pieces}
                    </CardDescription>
                  </div>
                  {isProduction() && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopyStatus}
                        data-testid="copy-status-btn"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Status
                      </Button>
                      {order.status !== 'Dispatched' && (
                        <Button 
                          size="sm"
                          onClick={handleMarkAsDispatched}
                          disabled={updating}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="mark-dispatched-btn"
                        >
                          {updating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <PackageCheck className="h-4 w-4 mr-2" />
                              Mark Dispatched
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Summary Row */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-slate-50 dark:bg-neutral-900">
                    <div className="text-center">
                      <p className="text-xs md:text-sm text-slate-500">Total Order</p>
                      <p className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">{order.pieces_ordered}</p>
                    </div>
                    {order.ready_from_inventory > 0 && (
                      <div className="text-center md:border-l md:border-r border-slate-200 dark:border-neutral-700">
                        <p className="text-xs md:text-sm text-emerald-600">From Inventory</p>
                        <p className="text-xl md:text-2xl font-bold text-emerald-600">{order.ready_from_inventory}</p>
                      </div>
                    )}
                    <div className="text-center col-span-2 md:col-span-1 pt-2 md:pt-0 border-t md:border-t-0 border-slate-200 dark:border-neutral-700">
                      <p className="text-xs md:text-sm text-amber-600">To Factory</p>
                      <p className="text-xl md:text-2xl font-bold text-amber-600">{order.pieces_to_factory}</p>
                    </div>
                  </div>

                  {/* Expected from Factory Date */}
                  {order.expected_from_factory_date && (
                    <div className="p-3 md:p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 md:h-10 md:w-10 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Truck className="h-4 w-4 md:h-5 md:w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-emerald-600 dark:text-emerald-400 font-medium">Expected from Factory</p>
                            <p className="text-base md:text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatDate(order.expected_from_factory_date)}</p>
                          </div>
                        </div>
                        <div className="text-left sm:text-right ml-12 sm:ml-0">
                          <p className="text-xs text-emerald-500">Days remaining</p>
                          <p className={cn(
                            "text-xl md:text-2xl font-bold",
                            getDaysUntil(order.expected_from_factory_date) < 0 ? "text-red-500" :
                            getDaysUntil(order.expected_from_factory_date) <= 2 ? "text-amber-500" :
                            "text-emerald-600 dark:text-emerald-400"
                          )}>
                            {getDaysUntil(order.expected_from_factory_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Overall Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Overall Progress</span>
                      <span className="font-semibold">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-3" />
                  </div>

                  {/* Editing Mode */}
                  {isEditingStages ? (
                    <div className="mt-6 space-y-4">
                      {/* Tally Validation Alert */}
                      <Alert variant={isTallyValid ? "default" : "destructive"} className={cn(
                        isTallyValid ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10" : ""
                      )}>
                        {isTallyValid ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        <AlertDescription className={isTallyValid ? "text-emerald-700 dark:text-emerald-400" : ""}>
                          {isTallyValid ? (
                            <span>Total pieces match order quantity ({order.pieces_ordered})</span>
                          ) : (
                            <span>
                              <strong>Mismatch:</strong> Current total is {editedTotal}, need {tallyDifference > 0 ? `${tallyDifference} more` : `${Math.abs(tallyDifference)} less`} to match order ({order.pieces_ordered})
                            </span>
                          )}
                        </AlertDescription>
                      </Alert>

                      {/* Editable Stage Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                        {PRODUCTION_STAGES.map((stage) => {
                          const currentQty = editedStageQuantities[stage] || 0;
                          const originalQty = order.stage_quantities?.find(sq => sq.stage === stage)?.quantity || 0;
                          const hasChanged = currentQty !== originalQty;
                          
                          return (
                            <div 
                              key={stage}
                              className={cn(
                                "p-2 md:p-3 rounded-lg border transition-all",
                                hasChanged ? "border-amber-400 bg-amber-50 dark:bg-amber-500/10" : "border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-900"
                              )}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[10px] md:text-xs text-slate-600 dark:text-slate-400 flex-1 truncate" title={stage}>
                                  {stage}
                                </p>
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-16 md:w-20 h-7 md:h-8 text-center font-bold text-sm"
                                  value={currentQty}
                                  onChange={(e) => handleStageQuantityChange(stage, e.target.value)}
                                  data-testid={`stage-input-${stage.replace(/\s+/g, '-').toLowerCase()}`}
                                />
                              </div>
                              {hasChanged && (
                                <p className="text-[10px] md:text-xs text-amber-600 mt-1">was: {originalQty}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Summary Footer */}
                      <div className={cn(
                        "p-3 md:p-4 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3",
                        isTallyValid ? "bg-emerald-100 dark:bg-emerald-500/20" : "bg-red-100 dark:bg-red-500/20"
                      )}>
                        <div>
                          <p className="text-xs md:text-sm font-medium">Total in Stages</p>
                          <p className={cn(
                            "text-xl md:text-2xl font-bold",
                            isTallyValid ? "text-emerald-600" : "text-red-600"
                          )}>
                            {editedTotal} / {order.pieces_ordered}
                          </p>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <Button 
                            variant="outline" 
                            onClick={handleCancelEditingStages}
                            disabled={updating}
                            className="flex-1 sm:flex-none text-xs md:text-sm"
                            size="sm"
                          >
                            <X className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSaveStageQuantities}
                            disabled={!isTallyValid || updating}
                            className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none text-xs md:text-sm"
                            data-testid="save-stages-btn"
                            size="sm"
                          >
                            {updating ? (
                              <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                            ) : (
                              <>
                                <Save className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                                Save
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* View Mode - Stage Quantities Display */
                    <div className="mt-6">
                      {/* Pending Receipts (factory dispatched, awaiting admin signature) */}
                      {order.pending_receipts?.filter(r => r.status === 'pending').length > 0 && (
                        <div className="mb-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-300 dark:border-amber-500/40" data-testid="pending-receipts-banner">
                          <div className="flex items-center gap-2 mb-3">
                            <PackageCheck className="h-5 w-5 text-amber-600" />
                            <h4 className="font-bold text-sm md:text-base text-amber-800 dark:text-amber-300">
                              {order.pending_receipts.filter(r => r.status === 'pending').length} Dispatch{order.pending_receipts.filter(r => r.status === 'pending').length > 1 ? 'es' : ''} Awaiting Your Signature
                            </h4>
                          </div>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
                            The factory has marked these pieces as dispatched. Sign to accept and unlock them in &quot;Received from Factory&quot;, or reject to send them back to Finished Goods.
                          </p>
                          <div className="space-y-2">
                            {order.pending_receipts.filter(r => r.status === 'pending').map(rec => (
                              <div key={rec.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-white dark:bg-neutral-900 rounded-md border border-amber-200 dark:border-amber-500/30" data-testid={`pending-receipt-${rec.id}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-base text-slate-900 dark:text-white">
                                    {rec.pieces} pcs <span className="text-xs font-normal text-slate-500">from {rec.from_substage}</span>
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Dispatched by {rec.dispatched_by} · {formatDateTime(rec.dispatched_at)}
                                    {rec.notes && <span className="italic"> — {rec.notes}</span>}
                                  </p>
                                </div>
                                {isProduction() && (
                                  <div className="flex gap-2 flex-shrink-0">
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openReceiptAction('accept', rec)} data-testid={`accept-receipt-${rec.id}`}>
                                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Accept
                                    </Button>
                                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400" onClick={() => openReceiptAction('reject', rec)} data-testid={`reject-receipt-${rec.id}`}>
                                      <X className="h-4 w-4 mr-1.5" /> Reject
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm">Piece Distribution by Stage</h4>
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          (order.stage_quantities?.reduce((sum, sq) => sum + sq.quantity, 0) || 0) === order.pieces_ordered
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                            : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                        )}>
                          Total: {order.stage_quantities?.reduce((sum, sq) => sum + sq.quantity, 0) || 0} / {order.pieces_ordered} pcs
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {order.stage_quantities?.filter(sq => sq.quantity > 0).map((sq) => {
                          const isOTF = sq.stage === "On the Floor";
                          const isCasting = sq.stage === "Casting";
                          const isFactoryOwned = isOTF || isCasting;
                          return (
                          <div 
                            key={sq.stage}
                            className={cn(
                              "p-3 rounded-lg border-l-4",
                              isFactoryOwned
                                ? "bg-blue-50 border-blue-500 dark:bg-blue-500/10"
                                : sq.stage === "Ready for Dispatch" || sq.stage === "Shipped" 
                                ? "bg-emerald-50 border-emerald-500 dark:bg-emerald-500/10" 
                                : "bg-amber-50 border-amber-500 dark:bg-amber-500/10"
                            )}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-xs text-slate-500 truncate">{sq.stage}</p>
                              {isFactoryOwned && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/30 dark:text-blue-300 font-semibold uppercase tracking-wide whitespace-nowrap" title="Owned by factory — admin view-only">
                                  Factory
                                </span>
                              )}
                            </div>
                            <p className="text-xl font-bold text-slate-900 dark:text-white">{sq.quantity}</p>
                            {/* Factory sub-stage breakdown for "On the Floor" */}
                            {isOTF && order.factory_sub_stages?.some(sub => sub.quantity > 0) && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {order.factory_sub_stages.filter(sub => sub.quantity > 0).map(sub => (
                                  <span key={sub.stage} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 font-medium">
                                    {sub.quantity} {sub.stage}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )})}
                      </div>
                      
                      {/* Show stages with 0 pieces in collapsed form */}
                      {order.stage_quantities?.some(sq => sq.quantity === 0) && (
                        <details className="mt-3">
                          <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                            Show empty stages ({order.stage_quantities?.filter(sq => sq.quantity === 0).length})
                          </summary>
                          <div className="grid grid-cols-4 gap-2 mt-2">
                            {order.stage_quantities?.filter(sq => sq.quantity === 0).map((sq) => (
                              <div key={sq.stage} className="p-2 rounded bg-slate-100 dark:bg-neutral-800 text-center">
                                <p className="text-xs text-slate-400 truncate">{sq.stage}</p>
                                <p className="text-sm font-medium text-slate-400">0</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Details & Materials Tabs */}
            <Tabs defaultValue="activity" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="materials" className="text-xs md:text-sm">Materials</TabsTrigger>
                <TabsTrigger value="dispatches" className="text-xs md:text-sm">Dispatches</TabsTrigger>
                <TabsTrigger value="activity" className="text-xs md:text-sm">Activity</TabsTrigger>
                <TabsTrigger
                  value="factory"
                  className={cn(
                    "text-xs md:text-sm relative",
                    // Red highlight when EITHER Manufacturing Order Form OR
                    // Factory Processing Sheet is missing on a factory-bound order.
                    order.factory_id && (order.pieces_to_factory || 0) > 0 && (!order.manufacturing_form || !order.factory_processing_sheet) &&
                      "data-[state=inactive]:bg-rose-50 data-[state=inactive]:text-rose-700 data-[state=inactive]:border data-[state=inactive]:border-rose-300 dark:data-[state=inactive]:bg-rose-500/10 dark:data-[state=inactive]:text-rose-300 dark:data-[state=inactive]:border-rose-500/40 data-[state=active]:bg-rose-600 data-[state=active]:text-white"
                  )}
                  data-testid="factory-tab-trigger"
                >
                  Factory
                  {order.factory_id && (order.pieces_to_factory || 0) > 0 && (!order.manufacturing_form || !order.factory_processing_sheet) && (
                    <span className="ml-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" aria-hidden />
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Activity Log Tab - Movement Tracking */}
              <TabsContent value="activity">
                <Card>
                  <CardContent className="p-4 md:p-6 space-y-6">
                    {/* Action Buttons */}
                    {isProduction() && (
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          onClick={() => setIsMovementDialogOpen(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="move-pieces-btn"
                        >
                          <MoveRight className="h-4 w-4 mr-2" />
                          Move Pieces
                        </Button>
                        <Button 
                          onClick={() => setIsIssuanceDialogOpen(true)}
                          variant="outline"
                          data-testid="issue-material-btn"
                        >
                          <Gem className="h-4 w-4 mr-2" />
                          Issue Material
                        </Button>
                        {/* Sticker Print Button */}
                        {!order.sticker_printed ? (
                          <Button
                            variant="outline"
                            className="border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-400 animate-pulse hover:animate-none"
                            onClick={() => setStickerDialogOpen(true)}
                            data-testid="sticker-print-btn"
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Sticker: Pending
                          </Button>
                        ) : (
                          <Badge variant="outline" className="h-9 px-3 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30 flex items-center gap-1.5" data-testid="sticker-done-badge">
                            <Printer className="h-3.5 w-3.5" /> Sticker Done
                          </Badge>
                        )}
                        {/* Gold Rate Cut Button */}
                        {(() => {
                          const isReady = (order.stage_quantities || []).some(sq => sq.stage === 'Ready to Ship' && sq.quantity > 0) ||
                            order.status === 'Ready to Ship' || order.status === 'Dispatched' || order.status === 'Partially Dispatched';
                          const hasRateCut = !!order.gold_rate_cut?.rate_type;
                          const canEditRateCut = !hasRateCut || user?.role === 'super_admin';
                          if (!isReady && !hasRateCut) return null;
                          return hasRateCut ? (
                            <Badge
                              variant="outline"
                              className={`h-9 px-3 bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 border-amber-200 dark:border-amber-500/30 flex items-center gap-1.5 transition-colors ${canEditRateCut ? 'cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-500/25' : ''}`}
                              onClick={canEditRateCut ? () => openGoldRateCutDialog() : undefined}
                              data-testid="rate-cut-done-badge"
                            >
                              <IndianRupee className="h-3.5 w-3.5" />
                              Rate: ₹{Number(order.gold_rate_cut.gold_rate_per_10g).toLocaleString('en-IN')}/10g
                            </Badge>
                          ) : (
                            <Button
                              variant="outline"
                              className="border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-400"
                              onClick={() => openGoldRateCutDialog()}
                              data-testid="rate-cut-btn"
                            >
                              <IndianRupee className="h-4 w-4 mr-2" />
                              Gold Rate Cut
                            </Button>
                          );
                        })()}
                      </div>
                    )}

                    {/* Material Status Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                        <p className="text-xs text-amber-600 font-medium mb-1">Gold Issued</p>
                        <p className="text-lg font-bold text-amber-700 dark:text-amber-300">
                          {parseFloat(order.materials?.gold?.total_issued || order.materials?.gold?.issued || 0).toFixed(2)}g / {parseFloat(order.materials?.gold?.total_required || order.materials?.gold?.required || order.order_gold_weight || 0).toFixed(2)}g
                        </p>
                        <p className="text-xs text-amber-500">
                          {order.materials?.gold?.pieces_issued || 0} / {order.pieces_to_factory || order.pieces_ordered} pcs covered
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30">
                        <p className="text-xs text-sky-600 font-medium mb-1">Diamonds Issued</p>
                        <p className="text-lg font-bold text-sky-700 dark:text-sky-300">
                          {parseFloat(order.materials?.diamonds?.total_issued || 0).toFixed(2)}ct / {parseFloat(order.materials?.diamonds?.total_required || order.materials?.diamonds?.weight || order.order_diamond_weight || 0).toFixed(2)}ct
                        </p>
                        <p className="text-xs text-sky-500">
                          {order.materials?.diamonds?.pieces_issued || 0} / {order.pieces_to_factory || order.pieces_ordered} pcs covered
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                        <p className="text-xs text-emerald-600 font-medium mb-1">Chains Issued</p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                          {order.materials?.chains?.total_issued || 0} / {order.total_chains_pieces || order.materials?.chains?.total_required || order.materials?.chains?.quantity_required || 0}
                        </p>
                        <p className="text-xs text-emerald-500">
                          {order.materials?.chains?.pieces_issued || 0} / {order.total_chains_pieces || 0} pcs needing chains
                        </p>
                      </div>
                    </div>

                    {/* Activity Timeline */}
                    <div>
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <History className="h-4 w-4 text-slate-500" />
                        Activity Timeline
                      </h4>
                      {order.movements && order.movements.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {[...order.movements].sort((a, b) => new Date(b.date || b.timestamp || 0) - new Date(a.date || a.timestamp || 0)).map((movement, i) => {
                            const isMove = movement.movement_type === 'stage_movement' || movement.movement_type === 'piece_movement' || movement.movement_type === 'factory_piece_movement';
                            const isRequest = movement.movement_type === 'factory_request';
                            const isResolved = movement.movement_type === 'factory_request_resolved';
                            const isIssuance = movement.movement_type === 'material_issuance' && movement.material_type;
                            const movementDate = movement.date || movement.timestamp;
                            const movementUser = movement.recorded_by || movement.user_name || 'System';
                            return (
                            <div key={movement.id || i} className={cn(
                              "flex gap-3 p-3 rounded-lg",
                              isRequest ? "bg-red-50 dark:bg-red-500/10" : isResolved ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-slate-50 dark:bg-neutral-800"
                            )}>
                              <div className={cn(
                                "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
                                isRequest ? "bg-red-100 dark:bg-red-500/20" :
                                isResolved ? "bg-emerald-100 dark:bg-emerald-500/20" :
                                isMove ? "bg-blue-100 dark:bg-blue-500/20" : "bg-amber-100 dark:bg-amber-500/20"
                              )}>
                                {isRequest ? <AlertTriangle className="h-4 w-4 text-red-600" /> :
                                 isResolved ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> :
                                 isMove ? <MoveRight className="h-4 w-4 text-blue-600" /> :
                                 <Gem className="h-4 w-4 text-amber-600" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    {isRequest ? (
                                      <p className="text-sm font-medium text-red-600 dark:text-red-400">
                                        Factory Request: {movement.request_type || movement.notes}
                                      </p>
                                    ) : isResolved ? (
                                      <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                        {movement.notes}
                                      </p>
                                    ) : isMove ? (
                                      <p className="text-sm font-medium">
                                        Moved <span className="text-blue-600">{movement.pieces} pcs</span>
                                        <span className="text-slate-400 mx-1">:</span>
                                        {movement.from_stage} <span className="text-slate-400">→</span> {movement.to_stage}
                                        {movement.movement_type === 'factory_piece_movement' && <span className="text-xs text-muted-foreground ml-1">(Factory)</span>}
                                      </p>
                                    ) : isIssuance ? (
                                      <p className="text-sm font-medium">
                                        <span className="text-amber-600">{movement.material_type.charAt(0).toUpperCase() + movement.material_type.slice(1)}</span> issued: {movement.quantity}{movement.material_type === 'gold' ? 'g' : (movement.material_type === 'diamonds' ? 'ct' : '')} for {movement.pieces_covered} pcs
                                      </p>
                                    ) : (
                                      <p className="text-sm font-medium capitalize">
                                        {(movement.movement_type || 'activity').replace(/_/g, ' ')}
                                      </p>
                                    )}
                                    {movement.notes && (
                                      <p className="text-xs text-slate-500 mt-0.5">{movement.notes}</p>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 whitespace-nowrap">
                                    {formatDateTime(movementDate)}
                                  </p>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">by {movementUser}</p>
                              </div>
                            </div>
                          )})}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No activity recorded yet</p>
                          <p className="text-xs mt-1">Use the buttons above to log movements and material issuances</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="factory">
                <Card>
                  <CardContent className="p-4 md:p-6 space-y-6">
                    {/* Manufacturing Order Form (PDF/Excel) gate — must be uploaded
                        before the factory can see this order on their portal for
                        acceptance. */}
                    <ManufacturingFormCard order={order} onChanged={fetchOrder} />

                    {/* Factory Processing Sheet (Excel) — same gate as Mfg Form. */}
                    <FactoryProcessingSheetCard order={order} onChanged={fetchOrder} />

                    {/* Factory Delivery Schedules */}
                    <div>
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-emerald-500" />
                        Factory Delivery Schedule
                      </h4>
                      {order.delivery_schedules?.length > 0 ? (
                        <div className="space-y-2">
                          {order.delivery_schedules.map((schedule, i) => (
                            <div key={schedule.id || i} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
                              <div>
                                <p className="font-semibold">{schedule.pieces} pieces</p>
                                <p className="text-sm text-slate-500">Expected: {formatDate(schedule.expected_date)}</p>
                                {schedule.notes && <p className="text-xs text-slate-400">{schedule.notes}</p>}
                              </div>
                              <Badge variant="outline" className="text-xs">{schedule.status || 'Scheduled'}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">No delivery schedules from factory yet</p>
                      )}
                    </div>

                    {/* Factory Requests with Chat */}
                    <div>
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        Factory Requests & Concerns
                        {order.factory_requests?.some(r => r.status === 'open') && (
                          <Badge className="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400 text-[10px] animate-pulse">
                            {order.factory_requests.filter(r => r.status === 'open').length} Open
                          </Badge>
                        )}
                      </h4>
                      {order.factory_requests?.length > 0 ? (
                        <div className="space-y-3">
                          {[...order.factory_requests].reverse().map((req) => (
                            <div 
                              key={req.id} 
                              className={cn(
                                "p-3 rounded-lg border",
                                req.status === 'open' 
                                  ? "bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30"
                                  : "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30"
                              )}
                              data-testid={`factory-request-${req.id}`}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                  <span className="font-semibold text-sm">{req.request_type}</span>
                                  <Badge className={cn("ml-2 text-[10px]", req.status === 'open' ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400")}>
                                    {req.status}
                                  </Badge>
                                </div>
                                {req.status === 'open' && isProduction() && (
                                  <Button
                                    size="sm" variant="outline"
                                    className="text-xs bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                    data-testid={`resolve-request-${req.id}`}
                                    onClick={async () => {
                                      try {
                                        await ordersApi.resolveRequest(order.id, req.id);
                                        toast.success('Request resolved');
                                        fetchOrder();
                                      } catch (e) {
                                        toast.error('Failed to resolve');
                                      }
                                    }}
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                                  </Button>
                                )}
                              </div>
                              {req.delay_reason && <p className="text-xs text-slate-600 dark:text-slate-400 mb-1"><strong>Reason:</strong> {req.delay_reason}</p>}
                              {req.style_numbers?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {req.style_numbers.map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">Style: {s}</Badge>)}
                                </div>
                              )}
                              <p className="text-[10px] text-muted-foreground mb-2">by {req.created_by} · {formatDate(req.created_at)}</p>
                              {/* Chat messages */}
                              {(req.messages || []).length > 0 && (
                                <div className="space-y-1.5 mb-2 max-h-[180px] overflow-y-auto border-t border-border pt-2">
                                  {req.messages.map(msg => (
                                    <div key={msg.id} className={cn("p-2 rounded text-xs", msg.sender_role === 'factory' ? "bg-white dark:bg-neutral-900 ml-0 mr-8" : "bg-blue-50 dark:bg-blue-500/10 ml-8 mr-0")}>
                                      <div className="flex justify-between">
                                        <span className="font-semibold">{msg.sender_name} <span className="font-normal text-muted-foreground">({msg.sender_role})</span></span>
                                        <span className="text-muted-foreground text-[10px]">{formatDate(msg.timestamp)}</span>
                                      </div>
                                      <p className="mt-0.5">{msg.message}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Chat input for open requests */}
                              {req.status === 'open' && (
                                <div className="flex gap-2 mt-2">
                                  <Input
                                    placeholder="Reply to factory..."
                                    value={requestChatId === req.id ? requestChatMsg : ''}
                                    onFocus={() => setRequestChatId(req.id)}
                                    onChange={(e) => { setRequestChatId(req.id); setRequestChatMsg(e.target.value); }}
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter' && requestChatMsg.trim()) {
                                        try {
                                          await ordersApi.addRequestMessage(order.id, req.id, { message: requestChatMsg });
                                          setRequestChatMsg('');
                                          fetchOrder();
                                        } catch (err) { toast.error('Failed to send'); }
                                      }
                                    }}
                                    className="h-8 text-xs"
                                  />
                                  <Button size="sm" className="h-8 px-2" onClick={async () => {
                                    if (!requestChatMsg.trim()) return;
                                    try {
                                      await ordersApi.addRequestMessage(order.id, req.id, { message: requestChatMsg });
                                      setRequestChatMsg('');
                                      fetchOrder();
                                    } catch (err) { toast.error('Failed to send'); }
                                  }}>
                                    <Send className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                              {req.status === 'resolved' && req.resolved_by && (
                                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">Resolved by {req.resolved_by}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-4">No factory requests</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="materials">
                <Card>
                  <CardContent className="p-4 md:p-6 space-y-4 md:space-y-6">
                    {/* Gold Tracking */}
                    <div className="p-3 md:p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-7 w-7 md:h-8 md:w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-amber-600 font-bold text-xs md:text-sm">Au</span>
                        </div>
                        <span className="font-semibold text-sm md:text-base">Gold</span>
                        {order.materials?.gold?.issued_status && (
                          <Badge className="bg-green-100 text-green-700 ml-auto text-xs">Issued</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 md:gap-4 text-sm">
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Required</p>
                          <p className="font-bold text-base md:text-lg">{parseFloat(order.materials?.gold?.required || 0).toFixed(2)}g</p>
                        </div>
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Issued</p>
                          <p className="font-bold text-base md:text-lg">{parseFloat(order.materials?.gold?.issued || 0).toFixed(2)}g</p>
                        </div>
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Balance</p>
                          <p className="font-bold text-base md:text-lg">{parseFloat(order.materials?.gold?.balance || 0).toFixed(2)}g</p>
                        </div>
                      </div>
                    </div>

                    {/* Diamonds */}
                    <div className="p-3 md:p-4 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Gem className="h-5 w-5 md:h-6 md:w-6 text-sky-500 flex-shrink-0" />
                        <span className="font-semibold text-sm md:text-base">Diamonds</span>
                        {order.materials?.diamonds?.issued && (
                          <Badge className="bg-green-100 text-green-700 ml-auto text-xs">Issued</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:gap-4 text-sm">
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Weight</p>
                          <p className="font-bold text-base md:text-lg">{parseFloat(order.materials?.diamonds?.weight || 0).toFixed(2)}ct</p>
                        </div>
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Issued</p>
                          <p className="font-bold text-base md:text-lg">{parseFloat(order.materials?.diamonds?.total_issued || 0).toFixed(2)}ct</p>
                        </div>
                      </div>
                      {/* Diamond issuance history */}
                      {order.materials?.diamonds?.issuances?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-sky-200 dark:border-sky-500/30 space-y-1.5">
                          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Issuance History</p>
                          {order.materials.diamonds.issuances.map((iss) => (
                            <div key={iss.id} className="flex items-center justify-between gap-2 text-xs bg-white dark:bg-neutral-900 rounded px-2.5 py-1.5" data-testid={`diamonds-issuance-${iss.id}`}>
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-sky-700 dark:text-sky-300">{parseFloat(iss.quantity).toFixed(2)}ct</span>
                                <span className="text-slate-500"> · {iss.pieces_covered} pcs</span>
                                <span className="text-slate-400"> · {formatDate(iss.date)}</span>
                                {iss.notes && <span className="text-slate-500 italic"> — {iss.notes}</span>}
                                {iss.edited_at && <span className="text-amber-600 ml-1 text-[10px]">(edited)</span>}
                              </div>
                              {isProduction() && (
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  <button onClick={() => openEditIssuance('diamonds', iss)} className="p-1 hover:bg-sky-100 dark:hover:bg-sky-500/20 rounded" aria-label="Edit issuance" data-testid={`edit-diamonds-issuance-${iss.id}`}>
                                    <Pencil className="h-3 w-3 text-sky-700 dark:text-sky-400" />
                                  </button>
                                  <button onClick={() => handleDeleteIssuance('diamonds', iss)} className="p-1 hover:bg-red-100 dark:hover:bg-red-500/20 rounded" aria-label="Delete issuance" data-testid={`delete-diamonds-issuance-${iss.id}`}>
                                    <Trash2 className="h-3 w-3 text-red-600 dark:text-red-400" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Chains */}
                    <div className="p-3 md:p-4 rounded-lg bg-slate-50 dark:bg-neutral-900 border">
                      <div className="flex items-center gap-2 mb-3">
                        <Link2 className="h-5 w-5 md:h-6 md:w-6 text-slate-500 flex-shrink-0" />
                        <span className="font-semibold text-sm md:text-base">Chains</span>
                        {order.materials?.chains?.issued_status && (
                          <Badge className="bg-green-100 text-green-700 ml-auto text-xs">Issued</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:gap-4 text-sm">
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Pieces Needing Chains</p>
                          <p className="font-bold text-base md:text-lg">{order.total_chains_pieces || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs md:text-sm text-slate-500">Chains Issued To</p>
                          <p className="font-bold text-base md:text-lg">{order.materials?.chains?.pieces_issued || 0} pcs</p>
                        </div>
                      </div>
                      {(order.chains_pieces_factory > 0) && (
                        <div className="mt-2 pt-2 border-t border-slate-200 dark:border-neutral-700 text-xs">
                          <div>
                            <p className="text-slate-500">Factory Pieces Needing Chains</p>
                            <p className="font-medium">{order.chains_pieces_factory || 0}</p>
                          </div>
                        </div>
                      )}
                      {/* Chain SKU Breakdown — assigned chains with live stock */}
                      {(order.materials?.chains?.breakdown?.length > 0) && (
                        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-neutral-700 space-y-1.5" data-testid="chain-breakdown-display">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Assigned Chain SKUs</p>
                            {(() => {
                              const totalAssigned = order.materials.chains.breakdown.reduce((s, b) => s + (parseInt(b.quantity || 0) || 0), 0);
                              const required = parseInt(order.chains_pieces_factory || 0) || 0;
                              const unassigned = Math.max(0, required - totalAssigned);
                              return (
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="text-[10px] border-slate-300">
                                    {totalAssigned}/{required} assigned
                                  </Badge>
                                  {unassigned > 0 && (
                                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">{unassigned} unassigned</Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          {order.materials.chains.breakdown.map((b, idx) => {
                            const sku = chainSkus.find(c => c.id === b.chain_sku_id);
                            const stock = sku?.quantity ?? null;
                            const required = parseInt(b.quantity || 0) || 0;
                            const short = stock !== null && stock < required;
                            return (
                              <div key={`${b.chain_sku_id}-${idx}`} className="flex items-center justify-between gap-2 text-xs bg-white dark:bg-neutral-900 rounded px-2.5 py-1.5 border border-slate-200 dark:border-neutral-700" data-testid={`chain-breakdown-row-${idx}`}>
                                <div className="flex-1 min-w-0">
                                  <span className="font-semibold text-slate-700 dark:text-slate-300 truncate block">
                                    {b.chain_label || sku?.chain_type || '—'}
                                  </span>
                                  <span className="text-[10px] text-slate-400 uppercase">
                                    {b.karat || sku?.karat || ''}{sku?.length ? ` · ${sku.length}` : ''}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Badge variant="outline" className="text-[10px] border-slate-300">
                                    need {required}
                                  </Badge>
                                  {stock === null ? (
                                    <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
                                      stock —
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className={cn(
                                      'text-[10px]',
                                      short
                                        ? 'border-rose-300 text-rose-700 bg-rose-50'
                                        : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                                    )}>
                                      stock {stock}{short ? ` · short ${required - stock}` : ''}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {/* One-click "Issue Chains" — deducts SKU stock and logs issuance */}
                          {(() => {
                            const breakdown = order.materials.chains.breakdown || [];
                            const totalAssigned = breakdown.reduce((s, b) => s + (parseInt(b.quantity || 0) || 0), 0);
                            const required = parseInt(order.chains_pieces_factory || 0) || 0;
                            const alreadyIssued = parseInt(order.materials?.chains?.pieces_issued || 0) || 0;
                            const remaining = Math.max(0, required - alreadyIssued);
                            // Any SKU short of stock?
                            const anyShort = breakdown.some(b => {
                              const sku = chainSkus.find(c => c.id === b.chain_sku_id);
                              const stock = sku?.quantity ?? null;
                              return stock !== null && stock < (parseInt(b.quantity || 0) || 0);
                            });
                            const overCap = totalAssigned > remaining;
                            const fullyIssued = remaining === 0;
                            const disabled = fullyIssued || totalAssigned === 0 || anyShort || overCap;
                            const reason = fullyIssued
                              ? 'All chain pieces already issued'
                              : totalAssigned === 0
                                ? 'Assign chain SKUs in Edit Order first'
                                : anyShort
                                  ? 'Insufficient stock — refill inventory or change SKU'
                                  : overCap
                                    ? `Breakdown (${totalAssigned}) exceeds remaining capacity (${remaining})`
                                    : null;
                            return (
                              <div className="mt-3 pt-3 border-t border-dashed border-slate-300 dark:border-neutral-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <p className="text-[11px] text-slate-500">
                                  Issue {totalAssigned} chain{totalAssigned === 1 ? '' : 's'} from inventory in one click — stock deducted automatically.
                                </p>
                                {isProduction() && (
                                  <Button
                                    size="sm"
                                    className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                    onClick={() => setIssueChainsConfirmOpen(true)}
                                    disabled={disabled || issuingChains}
                                    title={reason || `Issue ${totalAssigned} chains from inventory`}
                                    data-testid="issue-chains-from-breakdown-btn"
                                  >
                                    {issuingChains ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
                                    Issue Chains from Inventory
                                  </Button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {order.materials?.chains?.issuances?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-neutral-700 space-y-1.5">
                          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Issuance History</p>
                          {order.materials.chains.issuances.map((iss) => (
                            <div key={iss.id} className="flex items-center justify-between gap-2 text-xs bg-white dark:bg-neutral-900 rounded px-2.5 py-1.5 border border-slate-200 dark:border-neutral-700" data-testid={`chains-issuance-${iss.id}`}>
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-slate-700 dark:text-slate-300">{iss.quantity} chain{iss.quantity != 1 ? 's' : ''}</span>
                                <span className="text-slate-500"> · {iss.pieces_covered} pcs</span>
                                <span className="text-slate-400"> · {formatDate(iss.date)}</span>
                                {iss.notes && <span className="text-slate-500 italic"> — {iss.notes}</span>}
                                {iss.edited_at && <span className="text-amber-600 ml-1 text-[10px]">(edited)</span>}
                              </div>
                              {isProduction() && (
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  <button onClick={() => openEditIssuance('chains', iss)} className="p-1 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded" aria-label="Edit issuance" data-testid={`edit-chains-issuance-${iss.id}`}>
                                    <Pencil className="h-3 w-3 text-slate-700 dark:text-slate-300" />
                                  </button>
                                  <button onClick={() => handleDeleteIssuance('chains', iss)} className="p-1 hover:bg-red-100 dark:hover:bg-red-500/20 rounded" aria-label="Delete issuance" data-testid={`delete-chains-issuance-${iss.id}`}>
                                    <Trash2 className="h-3 w-3 text-red-600 dark:text-red-400" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="dispatches">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-base">Dispatches & Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(!order.dispatches || order.dispatches.length === 0) ? (
                      <div className="text-center py-8 text-sm text-muted-foreground">No dispatches recorded yet</div>
                    ) : (
                      <div className="space-y-3">
                        {order.dispatches.map((d) => (
                          <div key={d.id} className={cn(
                            "p-4 rounded-lg border",
                            d.invoiced 
                              ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30"
                              : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30"
                          )} data-testid={`dispatch-${d.id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-1 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={cn("text-xs font-bold px-2 py-0.5 rounded uppercase",
                                    d.dispatch_type === 'approval' ? "bg-amber-200 text-amber-800" : "bg-emerald-200 text-emerald-800"
                                  )}>
                                    {d.dispatch_type === 'approval' ? 'Approval Memo' : 'Invoice'}
                                  </span>
                                  <span className="text-sm font-semibold">{d.memo_number || d.invoice_number}</span>
                                  <span className="text-xs text-muted-foreground">{d.pieces} pcs</span>
                                  {d.bill_to_customer_name && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/40 text-[10px] font-medium" data-testid={`dispatch-billto-badge-${d.id}`}>
                                      Billed to: {d.bill_to_customer_name}
                                    </Badge>
                                  )}
                                </div>
                                {d.invoiced && d.total_amount > 0 && (
                                  <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Amount: {d.total_amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                                )}
                                {d.logistics && <p className="text-xs text-muted-foreground">Logistics: {d.logistics}</p>}
                                {d.tracking_number && <p className="text-xs text-muted-foreground">Tracking: {d.tracking_number}</p>}
                                {d.invoiced && d.dispatch_type === 'approval' && (
                                  <p className="text-xs text-emerald-600">Invoiced as {d.invoice_number} {d.invoiced_at ? `on ${formatDate(d.invoiced_at)}` : ''}</p>
                                )}
                                {d.invoice_logistics && <p className="text-xs text-muted-foreground">Invoice Logistics: {d.invoice_logistics}</p>}
                                {d.invoice_tracking && <p className="text-xs text-muted-foreground">Invoice Tracking: {d.invoice_tracking}</p>}
                                <p className="text-[10px] text-muted-foreground">{formatDate(d.created_at)} by {d.created_by}</p>
                              </div>
                              {d.dispatch_type === 'approval' && !d.invoiced && (
                                <Button size="sm" variant="outline" className="shrink-0"
                                  data-testid={`invoice-memo-${d.id}`}
                                  onClick={() => {
                                    if (!order?.gold_rate_cut?.rate_type) {
                                      toast.error('Gold rate must be cut before creating an invoice');
                                      return;
                                    }
                                    setInvoiceMemoDialog(d);
                                    setInvoiceForm({
                                      invoice_number: '',
                                      total_amount: '',
                                      logistics: d.logistics || '',
                                      tracking_number: d.tracking_number || '',
                                      gold_weight: parseFloat(order?.total_gold_weight || 0).toFixed(2),
                                      diamond_weight: parseFloat(order?.total_diamond_weight || 0).toFixed(2),
                                      bill_to_customer_id: '',
                                      bill_to_customer_name: '',
                                    });
                                    if (approvedCustomers.length === 0 && !loadingApprovedCustomers) {
                                      setLoadingApprovedCustomers(true);
                                      customersApi.getApprovedForOrder()
                                        .then(res => setApprovedCustomers(res.data || []))
                                        .catch(() => {})
                                        .finally(() => setLoadingApprovedCustomers(false));
                                    }
                                  }}
                                >
                                  Invoice This
                                </Button>
                              )}
                              {(user?.role === 'super_admin' || user?.role === 'admin') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="shrink-0 h-8 px-2"
                                  data-testid={`edit-dispatch-${d.id}`}
                                  onClick={() => setEditingDispatch(d)}
                                  title="Edit dispatch / invoice details"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {d.invoiced && d.invoice_number && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="shrink-0 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 border-amber-300 text-amber-800 dark:from-amber-500/10 dark:to-yellow-500/10 dark:border-amber-500/40 dark:text-amber-300"
                                  data-testid={`payforward-${d.id}`}
                                  onClick={() => downloadPayForward(d)}
                                  title="Download Siddhi PayForward early-payment supporting PDF"
                                >
                                  <FileText className="h-3.5 w-3.5 mr-1" /> PayForward PDF
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

            </Tabs>
          </div>

          {/* Right Column - Order Details Summary */}
          <div className="space-y-4 md:space-y-6">
            {/* Delivery Countdown */}
            <Card className={cn(
              !dispatched && daysUntil < 0 && "border-red-300 bg-red-50 dark:bg-red-500/10",
              !dispatched && daysUntil >= 0 && daysUntil <= 3 && "border-amber-300 bg-amber-50 dark:bg-amber-500/10",
              dispatched && "border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10"
            )}>
              <CardContent className="p-4 md:p-6 text-center">
                <Calendar className={cn(
                  "h-8 w-8 md:h-10 md:w-10 mx-auto mb-2 md:mb-3",
                  dispatched ? "text-emerald-500" :
                  daysUntil < 0 ? "text-red-500" : "text-amber-500"
                )} />
                <p className="text-xs md:text-sm text-slate-500 mb-1">Delivery Date</p>
                <p className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">
                  {formatDate(order.delivery_date)}
                </p>
                {dispatched ? (
                  <p className="text-xl md:text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">
                    {order.status}
                  </p>
                ) : (
                  <p className={cn(
                    "text-xl md:text-2xl font-bold mt-2",
                    daysUntil < 0 ? "text-red-500" : daysUntil <= 3 ? "text-amber-500" : "text-emerald-500"
                  )}>
                    {daysUntil > 0 ? `${daysUntil} days left` :
                     daysUntil === 0 ? 'Due Today!' :
                     `${Math.abs(daysUntil)} days overdue`}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Factory Deadline (admin-set, factory-only) */}
            <Card data-testid="factory-deadline-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Factory Deadline</p>
                    {order.factory_deadline ? (
                      <p className="text-lg font-bold mt-0.5">{formatDate(order.factory_deadline)}</p>
                    ) : (
                      <p className="text-sm italic text-muted-foreground mt-0.5">Not set — factory sees “No deadline set”</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">Shown only to the factory portal. Independent of customer delivery date.</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFactoryDeadlineOpen(true)}
                    data-testid="factory-deadline-set-btn"
                  >
                    {order.factory_deadline ? 'Edit' : 'Set'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Design Dependencies — below Delivery Countdown */}
            <DesignDependenciesCard orderId={order.id} onStateChange={setDesignState} />

            {/* Order Details Summary - Complete */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Customer</span>
                  <span className="font-medium">{order.customer_name}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Salesperson</span>
                  <span className="font-medium">{order.salesperson || '-'}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Factory</span>
                  <span className="font-medium">{order.factory_name}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total Pieces</span>
                  <span className="font-medium">{order.pieces_ordered}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">From Inventory</span>
                  <span className="font-medium text-emerald-600">{order.ready_from_inventory || 0}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">To Factory</span>
                  <span className="font-medium text-amber-600">{order.pieces_to_factory}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Diamond Quality</span>
                  <span className="font-medium">{order.diamond_quality || '-'}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Gold Karat</span>
                  <span className="font-medium">{order.gold_karat || '18kt'}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Certification</span>
                  <span className="font-medium">
                    {order.certification_lab === 'Other' 
                      ? order.certification_lab_other 
                      : order.certification_lab || '-'}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Cobranding</span>
                  {order.cobranding ? (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 text-xs">Yes</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">No</Badge>
                  )}
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Priority</span>
                  <Badge className={getPriorityClass(order.priority)} variant="outline">
                    {order.priority}
                  </Badge>
                </div>
                
                {/* Chain Pieces Info */}
                {(order.total_chains_pieces > 0) && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Factory Pieces with Chains</span>
                      <span className="font-medium">{order.total_chains_pieces}</span>
                    </div>
                  </>
                )}
                
                {/* Material Weight Breakup */}
                <Separator />
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-neutral-800 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Material Weights</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-slate-500">To Factory</p>
                      <p className="font-medium">{order.order_gold_weight || 0}g Gold</p>
                      <p className="font-medium">{order.order_diamond_weight || 0}ct Diamond</p>
                    </div>
                    <div>
                      <p className="text-slate-500">From Inventory</p>
                      <p className="font-medium">{order.inventory_gold_weight || 0}g Gold</p>
                      <p className="font-medium">{order.inventory_diamond_weight || 0}ct Diamond</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200 dark:border-neutral-700">
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-bold text-amber-600">{order.total_gold_weight || 0}g Gold, {order.total_diamond_weight || 0}ct Diamond</p>
                  </div>
                </div>

                {order.notes && (
                  <>
                    <Separator />
                    <div className="text-sm">
                      <span className="text-slate-500 block mb-1">Notes</span>
                      <p className="text-slate-700 dark:text-slate-300">{order.notes}</p>
                    </div>
                  </>
                )}
                
                <Separator />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Created: {formatDate(order.created_at)}</span>
                  <span>Updated: {formatDate(order.updated_at)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Order Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Order - {order.order_number}</DialogTitle>
            <DialogDescription>
              Edit any field — materials, stages, and ledger balances auto-sync on save. You&apos;ll be asked to confirm before the changes commit.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Pieces */}
            <div className="col-span-2 p-4 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 text-sm">Pieces</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Pieces Ordered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.pieces_ordered ?? ''}
                    onChange={(e) => setEditForm({...editForm, pieces_ordered: parseInt(e.target.value) || 0})}
                    data-testid="edit-pieces-ordered"
                  />
                  <p className="text-[11px] text-muted-foreground">Increase → new pcs go to &quot;Order Raised&quot;. Decrease → removed from &quot;Order Raised&quot; first.</p>
                </div>
                <div className="space-y-2">
                  <Label>Ready From Inventory</Label>
                  <Input
                    type="number"
                    min="0"
                    max={editForm.pieces_ordered || 0}
                    value={editForm.ready_from_inventory ?? ''}
                    onChange={(e) => setEditForm({...editForm, ready_from_inventory: parseInt(e.target.value) || 0})}
                    data-testid="edit-ready-from-inventory"
                  />
                  <p className="text-[11px] text-muted-foreground">Moved into &quot;From Inventory&quot; bucket. Cannot exceed total.</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Customer (approved) *</Label>
              <Popover open={customerComboOpen} onOpenChange={setCustomerComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between h-10 font-normal"
                    data-testid="edit-customer-combobox"
                  >
                    <span className="truncate">
                      {editForm.customer_name
                        ? `${editForm.customer_name}${editForm.branch ? ` — ${editForm.branch}` : ''}`
                        : (loadingApprovedCustomers ? 'Loading approved customers…' : 'Select an approved customer…')}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search customers..." className="h-10" />
                    <CommandList>
                      <CommandEmpty>
                        {approvedCustomers.length === 0
                          ? 'No approved customers yet. Approve KYC in the Customers page.'
                          : 'No customers match your search.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {approvedCustomers.map(c => (
                          <CommandItem
                            key={c.id}
                            value={`${c.company_name} ${c.contact_person_name || ''} ${c.mobile || ''}`}
                            onSelect={() => handleSelectCustomerInEdit(c)}
                            data-testid={`edit-customer-option-${c.id}`}
                          >
                            <div className="flex flex-col gap-0.5 w-full">
                              <span className="font-medium">{c.company_name}</span>
                              <span className="text-[11px] text-muted-foreground">
                                {c.contact_person_name || '—'} · {c.mobile || 'no mobile'}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {editForm.customer_id && (
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
                  ✓ Linked to approved KYC customer
                </p>
              )}
            </div>
            
            <div className="space-y-2 col-span-2">
              <WhatsAppRecipientPicker
                customerId={editForm.customer_id}
                value={editForm.notification_phones || []}
                onChange={(next) => {
                  // Mirror the "primary" (or first ticked) into customer_phone
                  // for backwards-compat with downstream reads.
                  setEditForm(prev => {
                    const primaryContactPhone = approvedCustomers.find(c => c.id === prev.customer_id)?.mobile || '';
                    const primaryTicked = next.find(p => p.phone === primaryContactPhone);
                    return {
                      ...prev,
                      notification_phones: next,
                      customer_phone: primaryTicked?.phone || next[0]?.phone || '',
                    };
                  });
                }}
                testIdPrefix="edit-order-recipient"
              />
            </div>

            <div className="space-y-2">
              <Label>Branch</Label>
              <Input
                value={editForm.branch || ''}
                onChange={(e) => setEditForm({...editForm, branch: e.target.value})}
                placeholder="e.g. Andheri, Surat"
                data-testid="edit-branch"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Salesperson</Label>
              <Input
                value={editForm.salesperson || ''}
                onChange={(e) => setEditForm({...editForm, salesperson: e.target.value})}
                data-testid="edit-salesperson"
              />
            </div>
            
            <div className="space-y-2">
              <Label>CO Number</Label>
              <Input
                value={editForm.co_number || ''}
                onChange={(e) => setEditForm({...editForm, co_number: e.target.value})}
                placeholder="Manual CO number"
                data-testid="edit-co-number"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Factory</Label>
              <Select 
                value={editForm.factory_id} 
                onValueChange={(value) => setEditForm({...editForm, factory_id: value})}
              >
                <SelectTrigger data-testid="edit-factory-select">
                  <SelectValue placeholder="Select factory" />
                </SelectTrigger>
                <SelectContent>
                  {factories.map(factory => (
                    <SelectItem key={factory.id} value={factory.id}>
                      {factory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Diamond Quality</Label>
              <Select 
                value={editForm.diamond_quality} 
                onValueChange={(value) => setEditForm({...editForm, diamond_quality: value})}
              >
                <SelectTrigger data-testid="edit-diamond-quality">
                  <SelectValue placeholder="Select quality" />
                </SelectTrigger>
                <SelectContent>
                  {DIAMOND_QUALITIES.map(q => (
                    <SelectItem key={q} value={q}>{q}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Gold Karat</Label>
              <Select 
                value={editForm.gold_karat} 
                onValueChange={(value) => setEditForm({...editForm, gold_karat: value})}
              >
                <SelectTrigger data-testid="edit-gold-karat">
                  <SelectValue placeholder="Select karat" />
                </SelectTrigger>
                <SelectContent>
                  {GOLD_KARATS.map(k => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Certification Lab</Label>
              <Select 
                value={editForm.certification_lab} 
                onValueChange={(value) => setEditForm({...editForm, certification_lab: value, certification_lab_other: value !== 'Other' ? '' : editForm.certification_lab_other})}
              >
                <SelectTrigger data-testid="edit-cert-lab">
                  <SelectValue placeholder="Select lab" />
                </SelectTrigger>
                <SelectContent>
                  {CERTIFICATION_LABS.map(lab => (
                    <SelectItem key={lab} value={lab}>{lab}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {editForm.certification_lab === 'Other' && (
              <div className="space-y-2">
                <Label>Other Lab Name</Label>
                <Input
                  value={editForm.certification_lab_other || ''}
                  onChange={(e) => setEditForm({...editForm, certification_lab_other: e.target.value})}
                  placeholder="Enter lab name"
                />
              </div>
            )}
            
            {/* Material Weights - Order (To Factory) */}
            <div className="col-span-2 p-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-3 text-sm">By Order (To Factory)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gold Weight (g)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.order_gold_weight || ''}
                    onChange={(e) => setEditForm({...editForm, order_gold_weight: parseFloat(e.target.value) || 0})}
                    data-testid="edit-order-gold-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Diamond Weight (ct)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.order_diamond_weight || ''}
                    onChange={(e) => setEditForm({...editForm, order_diamond_weight: parseFloat(e.target.value) || 0})}
                    data-testid="edit-order-diamond-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pieces with Chains</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.chains_pieces_factory || ''}
                    onChange={(e) => setEditForm({...editForm, chains_pieces_factory: parseInt(e.target.value) || 0})}
                    data-testid="edit-chains-pieces-factory"
                  />
                </div>
              </div>
              {parseInt(editForm.chains_pieces_factory || 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-300/50 dark:border-amber-500/30">
                  <ChainBreakdownPicker
                    value={editForm.chain_breakdown}
                    onChange={(v) => setEditForm({ ...editForm, chain_breakdown: v })}
                    karat={editForm.gold_karat || order.gold_karat}
                    totalRequired={editForm.chains_pieces_factory}
                  />
                </div>
              )}
            </div>
            
            {/* Material Weights - Inventory */}
            <div className="col-span-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30">
              <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-3 text-sm">From Inventory</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gold Weight (g)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.inventory_gold_weight || ''}
                    onChange={(e) => setEditForm({...editForm, inventory_gold_weight: parseFloat(e.target.value) || 0})}
                    data-testid="edit-inventory-gold-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Diamond Weight (ct)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.inventory_diamond_weight || ''}
                    onChange={(e) => setEditForm({...editForm, inventory_diamond_weight: parseFloat(e.target.value) || 0})}
                    data-testid="edit-inventory-diamond-weight"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Pieces with Chains (Inventory)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.chains_pieces_inventory || ''}
                    onChange={(e) => setEditForm({...editForm, chains_pieces_inventory: parseInt(e.target.value) || 0})}
                    data-testid="edit-chains-pieces-inventory"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={editForm.priority} 
                onValueChange={(value) => setEditForm({...editForm, priority: value})}
              >
                <SelectTrigger data-testid="edit-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Delivery Date</Label>
              <Input
                type="date"
                value={editForm.delivery_date || ''}
                onChange={(e) => setEditForm({...editForm, delivery_date: e.target.value})}
                data-testid="edit-delivery-date"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({...editForm, status: value})}
              >
                <SelectTrigger data-testid="edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCTION_STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  <SelectItem value="Order Received">Order Received</SelectItem>
                  <SelectItem value="Ready for Dispatch">Ready for Dispatch</SelectItem>
                  <SelectItem value="Partially Dispatched">Partially Dispatched</SelectItem>
                  <SelectItem value="Shipped">Shipped</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">⚠️ Manually changing status bypasses stage workflow. Use with caution.</p>
            </div>
            
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="edit-cobranding"
                checked={editForm.cobranding || false}
                onChange={(e) => setEditForm({...editForm, cobranding: e.target.checked})}
                className="h-4 w-4"
              />
              <Label htmlFor="edit-cobranding">Co-branding Required</Label>
            </div>

            <div className="col-span-2 p-3 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/40 dark:bg-indigo-500/5">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  id="edit-is-stock-order"
                  checked={editForm.is_stock_order || false}
                  onChange={(e) => setEditForm({...editForm, is_stock_order: e.target.checked})}
                  className="h-4 w-4 mt-0.5 text-indigo-600"
                  data-testid="edit-is-stock-checkbox"
                />
                <div>
                  <Label htmlFor="edit-is-stock-order" className="font-medium cursor-pointer">Stock Order (internal, not for customer)</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Hidden from Receivables. Use "Mark as Stocked & Close" on the order page to finalise without dispatching.
                  </p>
                </div>
              </label>
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Order notes..."
                rows={3}
                data-testid="edit-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOrder} disabled={updating} data-testid="save-order-btn">
              <Save className="h-4 w-4 mr-2" />
              Review Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Typed-confirmation Dialog — user must type CHANGES to commit sensitive edits */}
      <Dialog open={confirmEditOpen} onOpenChange={(v) => { if (!updating) { setConfirmEditOpen(v); if (!v) setConfirmText(''); } }}>
        <DialogContent className="max-w-lg" data-testid="confirm-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" /> Review &amp; Confirm Order Edit
            </DialogTitle>
            <DialogDescription>
              These changes will cascade across materials, stages, and ledger balances. Review carefully before confirming.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[50vh] overflow-y-auto space-y-2 py-2">
            {computeDiff().length === 0 ? (
              <p className="text-sm text-muted-foreground">No changes detected.</p>
            ) : (
              computeDiff().map(d => (
                <div
                  key={d.key}
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm",
                    d.sensitive
                      ? "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30"
                      : "bg-slate-50 dark:bg-neutral-800 border-slate-200 dark:border-neutral-700"
                  )}
                  data-testid={`diff-row-${d.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{d.label}</span>
                    {d.sensitive && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400">Sensitive</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    <span className="line-through opacity-70">{String(d.before ?? '—') || '—'}</span>
                    <ArrowRight className="h-3 w-3 inline mx-1.5 align-text-top" />
                    <span className="font-semibold text-foreground">{String(d.after ?? '—') || '—'}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-3 rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs space-y-1">
            <p className="font-semibold text-red-700 dark:text-red-400">Cascading effects:</p>
            <ul className="list-disc pl-4 text-red-600 dark:text-red-400 space-y-0.5">
              <li>Material requirements (gold/diamond/chains) will recompute against new weights</li>
              <li>Stage buckets will rebalance via &quot;Order Raised&quot; / &quot;From Inventory&quot;</li>
              <li>Pending gold in Metal Ledger will adjust automatically</li>
              <li>A detailed audit entry is written to the activity log</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label>Type <code className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 font-bold">CHANGES</code> to confirm</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CHANGES"
              autoFocus
              data-testid="confirm-changes-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmEditOpen(false)} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmedSave}
              disabled={updating || confirmText !== 'CHANGES' || computeDiff().length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-save-btn"
            >
              {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Commit Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Chains from Breakdown Confirmation Dialog */}
      <Dialog open={issueChainsConfirmOpen} onOpenChange={(v) => { if (!issuingChains) setIssueChainsConfirmOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="issue-chains-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-600" /> Issue Chains from Inventory
            </DialogTitle>
            <DialogDescription>
              The assigned chain SKUs will be deducted from inventory and an issuance entry will be logged on this order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(order?.materials?.chains?.breakdown || []).map((b, idx) => {
              const sku = chainSkus.find(c => c.id === b.chain_sku_id);
              const stock = sku?.quantity ?? null;
              const qty = parseInt(b.quantity || 0) || 0;
              const newStock = stock !== null ? stock - qty : null;
              return (
                <div key={idx} className="flex items-center justify-between text-sm border rounded px-3 py-2 bg-slate-50 dark:bg-neutral-900" data-testid={`issue-confirm-row-${idx}`}>
                  <div>
                    <p className="font-semibold">{b.chain_label || sku?.chain_type || '—'}</p>
                    <p className="text-[11px] text-slate-500">Deduct {qty} from stock</p>
                  </div>
                  {stock !== null && (
                    <Badge variant="outline" className="text-[10px]">
                      {stock} → {newStock}
                    </Badge>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
              This action cannot be undone in one click — to reverse, edit the issuance entry or manually add stock back.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueChainsConfirmOpen(false)} disabled={issuingChains} data-testid="issue-chains-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleIssueChainsFromBreakdown}
              disabled={issuingChains}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="issue-chains-confirm"
            >
              {issuingChains ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              Confirm Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Stocked & Close Dialog */}
      <Dialog open={moveToStockDialogOpen} onOpenChange={(v) => !movingToStock && setMoveToStockDialogOpen(v)}>
        <DialogContent className="max-w-md" data-testid="move-to-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-indigo-600" /> Mark as Stocked & Close
            </DialogTitle>
            <DialogDescription>
              Record an internal stock-transfer dispatch and close this order. No invoice is generated;
              the order stays out of Receivables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Pieces moved to stock *</Label>
              <Input
                type="number"
                min={1}
                value={moveToStockPieces || ''}
                onChange={(e) => setMoveToStockPieces(e.target.value)}
                placeholder={`Up to ${order?.pieces_ordered || 0}`}
                data-testid="move-to-stock-pieces"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={moveToStockNotes}
                onChange={(e) => setMoveToStockNotes(e.target.value)}
                placeholder="Anything to note (location, batch ref, etc.)"
                data-testid="move-to-stock-notes"
              />
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-400">
              This will set the status to <strong>Dispatched</strong> and add a STOCK-prefixed memo number.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveToStockDialogOpen(false)} disabled={movingToStock}>Cancel</Button>
            <Button onClick={handleMoveToStock} disabled={movingToStock} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="move-to-stock-confirm">
              {movingToStock ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Boxes className="h-4 w-4 mr-2" />}
              Confirm Stock Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Pieces Dialog */}
      <Dialog open={isMovementDialogOpen} onOpenChange={setIsMovementDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Move Pieces</DialogTitle>
            <DialogDescription>
              Log movement of pieces from one stage to another. <span className="font-medium text-amber-700 dark:text-amber-400">&quot;Casting&quot; and &quot;On the Floor&quot; are owned by the factory</span> — the factory pulls pieces from Order Raised, processes them, and dispatches them. You&apos;ll get a pending receipt to sign for each dispatch.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>From Stage *</Label>
              <Select 
                value={movementData.from_stage} 
                onValueChange={(value) => setMovementData({...movementData, from_stage: value})}
              >
                <SelectTrigger data-testid="movement-from-stage">
                  <SelectValue placeholder="Select source stage" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCTION_STAGES.filter(s => s !== 'On the Floor' && s !== 'Casting').map(stage => {
                    const stageQty = order?.stage_quantities?.find(sq => sq.stage === stage)?.quantity || 0;
                    return (
                      <SelectItem key={stage} value={stage} disabled={stageQty === 0}>
                        {stage} ({stageQty} pcs)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>To Stage *</Label>
              <Select 
                value={movementData.to_stage} 
                onValueChange={(value) => setMovementData({...movementData, to_stage: value})}
              >
                <SelectTrigger data-testid="movement-to-stage">
                  <SelectValue placeholder="Select destination stage" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCTION_STAGES.filter(s => s !== 'On the Floor' && s !== 'Casting').map(stage => (
                    <SelectItem key={stage} value={stage} disabled={stage === movementData.from_stage || stage === 'Received from Factory'}>
                      {stage}{stage === 'Received from Factory' ? ' — set via factory dispatch + signing only' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Number of Pieces *</Label>
              <Input
                type="number"
                value={movementData.pieces}
                onChange={(e) => setMovementData({...movementData, pieces: e.target.value})}
                placeholder="How many pieces to move?"
                data-testid="movement-pieces"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={movementData.notes}
                onChange={(e) => setMovementData({...movementData, notes: e.target.value})}
                placeholder="Any notes about this movement..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMovementDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogMovement} disabled={updating} data-testid="confirm-movement-btn">
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MoveRight className="h-4 w-4 mr-2" />
              )}
              Log Movement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Material Dialog */}
      <Dialog open={isIssuanceDialogOpen} onOpenChange={setIsIssuanceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Material</DialogTitle>
            <DialogDescription>
              Log material issuance for this order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Material Type *</Label>
              <Select 
                value={issuanceData.material_type} 
                onValueChange={(value) => setIssuanceData({...issuanceData, material_type: value})}
              >
                <SelectTrigger data-testid="issuance-material-type">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gold" disabled>
                    Gold (Use Metal Management)
                  </SelectItem>
                  <SelectItem value="diamonds" disabled={order?.materials?.diamonds?.issued_status || order?.materials?.diamonds?.issued === true}>
                    Diamonds {(order?.materials?.diamonds?.issued_status || order?.materials?.diamonds?.issued === true) ? '(Fully Issued)' : ''}
                  </SelectItem>
                  <SelectItem value="chains" disabled={order?.materials?.chains?.issued_status || order?.total_chains_pieces === 0}>
                    Chains {order?.total_chains_pieces === 0 ? '(No pieces need chains)' : (order?.materials?.chains?.issued_status ? '(Fully Issued)' : '')}
                  </SelectItem>
                </SelectContent>
              </Select>
              {/* Gold redirect to Metal Management */}
              {!order?.materials?.gold?.issued_status && (
                <button
                  onClick={() => navigate('/metal-ledger')}
                  className="w-full mt-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-left hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
                  data-testid="go-to-metal-mgmt"
                >
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Gold issuance is managed via Metal Management</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-500">Click here to issue gold in bulk to the factory →</p>
                </button>
              )}
            </div>

            {/* Show remaining capacity */}
            {(() => {
              const mat = order?.materials?.[issuanceData.material_type] || {};
              const isGold = issuanceData.material_type === 'gold';
              const isDiamonds = issuanceData.material_type === 'diamonds';
              const maxQty = isGold ? (mat.required || mat.total_required || 0) : (isDiamonds ? (mat.weight || mat.total_required || 0) : (order?.total_chains_pieces || 0));
              const issuedQty = mat.total_issued || 0;
              const remainingQty = Math.max(0, maxQty - issuedQty);
              const maxPcs = issuanceData.material_type === 'chains' ? (order?.total_chains_pieces || 0) : (order?.pieces_to_factory || order?.pieces_ordered || 0);
              const issuedPcs = mat.pieces_issued || 0;
              const remainingPcs = Math.max(0, maxPcs - issuedPcs);
              const unit = isGold ? 'g' : (isDiamonds ? 'ct' : 'pcs');

              return (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Remaining Quantity</p>
                      <p className="font-bold text-amber-800 dark:text-amber-300">{remainingQty.toFixed(2)}{unit} <span className="font-normal text-xs text-amber-500">of {maxQty.toFixed(2)}{unit}</span></p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Remaining Pieces</p>
                      <p className="font-bold text-amber-800 dark:text-amber-300">{remainingPcs} pcs <span className="font-normal text-xs text-amber-500">of {maxPcs} pcs</span></p>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <div className="space-y-2">
              <Label>
                Quantity * ({issuanceData.material_type === 'gold' ? 'grams' : (issuanceData.material_type === 'diamonds' ? 'carats' : 'pieces')})
              </Label>
              <Input
                type="number"
                step="0.01"
                value={issuanceData.quantity}
                onChange={(e) => setIssuanceData({...issuanceData, quantity: e.target.value})}
                placeholder={`Enter ${issuanceData.material_type === 'gold' ? 'weight in grams' : (issuanceData.material_type === 'diamonds' ? 'weight in carats' : 'quantity')}`}
                data-testid="issuance-quantity"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Pieces Covered *</Label>
              <Input
                type="number"
                value={issuanceData.pieces_covered}
                onChange={(e) => setIssuanceData({...issuanceData, pieces_covered: e.target.value})}
                placeholder="How many pieces does this cover?"
                data-testid="issuance-pieces"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={issuanceData.notes}
                onChange={(e) => setIssuanceData({...issuanceData, notes: e.target.value})}
                placeholder="Any notes about this issuance..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIssuanceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogIssuance} disabled={updating} data-testid="confirm-issuance-btn">
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Gem className="h-4 w-4 mr-2" />
              )}
              Log Issuance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticker Print Confirmation Dialog */}
      <Dialog open={stickerDialogOpen} onOpenChange={setStickerDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Sticker Print Confirmation</DialogTitle>
            <DialogDescription>
              Order <strong>{order?.order_number}</strong> — Has the sticker been printed?
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Pieces cannot be moved out of "Order Raised" stage until the sticker is marked as printed.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setStickerDialogOpen(false)} disabled={updating}>Not Yet</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={updating} onClick={handleStickerConfirm} data-testid="sticker-confirm-detail-btn">
              {updating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Yes, Printed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Type Choice Dialog */}
      <Dialog open={dispatchDialogOpen} onOpenChange={(v) => { if (!updating && !pendingMovement) { setDispatchDialogOpen(v); setDispatchStep('choose'); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onInteractOutside={e => { if (pendingMovement) e.preventDefault(); }}>
          {dispatchStep === 'choose' ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Dispatch Type</DialogTitle>
                <DialogDescription>
                  {dispatchPieces} pieces moved to Dispatched. How is this being sent?
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-4">
                <button onClick={() => setDispatchStep('approval')} className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-500/10 hover:border-amber-500 transition-all text-center" data-testid="dispatch-type-approval">
                  <Package className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                  <p className="font-semibold text-sm">Approval Memo</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Sent on approval, invoice later</p>
                </button>
                <button onClick={() => {
                  if (!order?.gold_rate_cut?.rate_type) {
                    toast.error('Gold rate must be cut before creating an invoice');
                    return;
                  }
                  setDispatchStep('invoice');
                  // Lazy-load approved customers for the Bill-To picker.
                  if (approvedCustomers.length === 0 && !loadingApprovedCustomers) {
                    setLoadingApprovedCustomers(true);
                    customersApi.getApprovedForOrder()
                      .then(res => setApprovedCustomers(res.data || []))
                      .catch(() => {})
                      .finally(() => setLoadingApprovedCustomers(false));
                  }
                }} className="p-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 hover:border-emerald-500 transition-all text-center" data-testid="dispatch-type-invoice">
                  <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="font-semibold text-sm">Direct Invoice</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Invoiced and dispatched</p>
                </button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDispatchDialogOpen(false)} className="w-full text-muted-foreground">Cancel</Button>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{dispatchStep === 'approval' ? 'Approval Memo Details' : 'Invoice Details'}</DialogTitle>
                <DialogDescription>{dispatchPieces} pieces</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {dispatchStep === 'approval' ? (
                  <div className="space-y-1">
                    <Label>Approval Memo Number *</Label>
                    <Input value={dispatchForm.memo_number} onChange={e => setDispatchForm(p => ({...p, memo_number: e.target.value}))} placeholder="e.g. AM-2026-001" data-testid="dispatch-memo-number" />
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      <Label>Invoice Number *</Label>
                      <Input value={dispatchForm.invoice_number} onChange={e => setDispatchForm(p => ({...p, invoice_number: e.target.value}))} placeholder="e.g. INV-2026-001" data-testid="dispatch-invoice-number" />
                    </div>
                    <div className="space-y-1">
                      <Label>Total Amount (Rs)</Label>
                      <Input type="number" value={dispatchForm.total_amount} onChange={e => setDispatchForm(p => ({...p, total_amount: e.target.value}))} placeholder="0" data-testid="dispatch-total-amount" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>Gold Weight (gm)</Label>
                        <Input type="number" step="0.001" value={dispatchForm.gold_weight} onChange={e => setDispatchForm(p => ({...p, gold_weight: e.target.value}))} placeholder="0.000" data-testid="dispatch-gold-weight" />
                      </div>
                      <div className="space-y-1">
                        <Label>Diamond Weight (ct)</Label>
                        <Input type="number" step="0.001" value={dispatchForm.diamond_weight} onChange={e => setDispatchForm(p => ({...p, diamond_weight: e.target.value}))} placeholder="0.000" data-testid="dispatch-diamond-weight" />
                      </div>
                    </div>
                  </>
                )}
                <div className="space-y-1">
                  <Label>Sent Via (Logistics)</Label>
                  <Input value={dispatchForm.logistics} onChange={e => setDispatchForm(p => ({...p, logistics: e.target.value}))} placeholder="e.g. Blue Dart, DTDC" data-testid="dispatch-logistics" />
                </div>
                <div className="space-y-1">
                  <Label>Tracking Number</Label>
                  <Input value={dispatchForm.tracking_number} onChange={e => setDispatchForm(p => ({...p, tracking_number: e.target.value}))} placeholder="Tracking ID" data-testid="dispatch-tracking" />
                </div>
                {dispatchStep === 'invoice' && (
                  <BillToPicker
                    open={billToDispatchOpen}
                    setOpen={setBillToDispatchOpen}
                    approvedCustomers={approvedCustomers}
                    loading={loadingApprovedCustomers}
                    orderCustomerName={order?.customer_name}
                    orderCustomerId={order?.customer_id}
                    value={{ id: dispatchForm.bill_to_customer_id, name: dispatchForm.bill_to_customer_name }}
                    onChange={(cust) => setDispatchForm(p => ({ ...p, bill_to_customer_id: cust?.id || '', bill_to_customer_name: cust?.company_name || '' }))}
                    testIdPrefix="dispatch-billto"
                  />
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDispatchStep('choose')}>Back</Button>
                <Button onClick={handleSubmitDispatch} disabled={updating} data-testid="dispatch-submit-btn">
                  {updating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert Approval Memo to Invoice Dialog */}
      <Dialog open={!!invoiceMemoDialog} onOpenChange={(v) => { if (!v) setInvoiceMemoDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Invoice Approval Memo</DialogTitle>
            <DialogDescription>
              Converting memo <strong>{invoiceMemoDialog?.memo_number}</strong> ({invoiceMemoDialog?.pieces} pcs) to invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Invoice Number *</Label>
              <Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(p => ({...p, invoice_number: e.target.value}))} placeholder="e.g. INV-2026-001" data-testid="memo-invoice-number" />
            </div>
            <div className="space-y-1">
              <Label>Total Amount (Rs)</Label>
              <Input type="number" value={invoiceForm.total_amount} onChange={e => setInvoiceForm(p => ({...p, total_amount: e.target.value}))} placeholder="0" data-testid="memo-total-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Gold Weight (gm)</Label>
                <Input type="number" step="0.001" value={invoiceForm.gold_weight} onChange={e => setInvoiceForm(p => ({...p, gold_weight: e.target.value}))} placeholder="0.000" />
              </div>
              <div className="space-y-1">
                <Label>Diamond Weight (ct)</Label>
                <Input type="number" step="0.001" value={invoiceForm.diamond_weight} onChange={e => setInvoiceForm(p => ({...p, diamond_weight: e.target.value}))} placeholder="0.000" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Sent Via (Logistics)</Label>
              <Input value={invoiceForm.logistics} onChange={e => setInvoiceForm(p => ({...p, logistics: e.target.value}))} placeholder={invoiceMemoDialog?.logistics || 'e.g. Blue Dart'} data-testid="memo-invoice-logistics" />
            </div>
            <div className="space-y-1">
              <Label>Tracking Number</Label>
              <Input value={invoiceForm.tracking_number} onChange={e => setInvoiceForm(p => ({...p, tracking_number: e.target.value}))} placeholder={invoiceMemoDialog?.tracking_number || 'Tracking ID'} data-testid="memo-invoice-tracking" />
            </div>
            <BillToPicker
              open={billToMemoOpen}
              setOpen={setBillToMemoOpen}
              approvedCustomers={approvedCustomers}
              loading={loadingApprovedCustomers}
              orderCustomerName={order?.customer_name}
              orderCustomerId={order?.customer_id}
              value={{ id: invoiceForm.bill_to_customer_id, name: invoiceForm.bill_to_customer_name }}
              onChange={(cust) => setInvoiceForm(p => ({ ...p, bill_to_customer_id: cust?.id || '', bill_to_customer_name: cust?.company_name || '' }))}
              testIdPrefix="memo-billto"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceMemoDialog(null)}>Cancel</Button>
            <Button onClick={handleConvertMemoToInvoice} disabled={updating} data-testid="memo-invoice-submit">
              {updating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Convert to Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gold Rate Cut Dialog */}
      <Dialog open={goldRateCutDialogOpen} onOpenChange={(v) => { if (!savingRateCut) setGoldRateCutDialogOpen(v); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-amber-500" />
              Gold Rate Cut — {order?.order_number}
            </DialogTitle>
            <DialogDescription>Record the gold rate cut before dispatch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Rate Type *</Label>
              <Select value={rateCutForm.rate_type} onValueChange={v => setRateCutForm(f => ({...f, rate_type: v}))}>
                <SelectTrigger data-testid="detail-rate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="995_gst">995 with GST</SelectItem>
                  <SelectItem value="999_gst">999 with GST</SelectItem>
                  <SelectItem value="other">Other (specify)</SelectItem>
                </SelectContent>
              </Select>
              {rateCutForm.rate_type === 'other' && (
                <Input placeholder="Specify rate type..." value={rateCutForm.rate_type_other} onChange={e => setRateCutForm(f => ({...f, rate_type_other: e.target.value}))} className="mt-2" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Gold Rate per 10gm (Rs) *</Label>
              <Input type="number" min="0" step="1" value={rateCutForm.gold_rate_per_10g} onChange={e => setRateCutForm(f => ({...f, gold_rate_per_10g: e.target.value}))} placeholder="e.g. 72500" data-testid="detail-gold-rate-input" />
            </div>
            <div className="space-y-2">
              <Label>Upload Rate Cut Screenshot</Label>
              <Input type="file" accept="image/*" onChange={e => setRateCutFile(e.target.files[0])} data-testid="detail-rate-cut-file-input" />
              {(order?.gold_rate_cut?.screenshot_url || order?.gold_rate_cut?.screenshot_path) && !rateCutFile && (
                <a href={order.gold_rate_cut.screenshot_url || ordersApi.getGoldRateScreenshotUrl(id)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">View existing screenshot</a>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={rateCutForm.notes} onChange={e => setRateCutForm(f => ({...f, notes: e.target.value}))} placeholder="Any notes..." />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGoldRateCutDialogOpen(false)} disabled={savingRateCut}>Cancel</Button>
            <Button onClick={handleSaveGoldRateCut} disabled={savingRateCut} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="detail-save-rate-cut-btn">
              {savingRateCut ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <IndianRupee className="h-4 w-4 mr-1" />}
              {order?.gold_rate_cut?.rate_type ? 'Update' : 'Save'} Rate Cut
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Manual Send Dialog */}
      <Dialog open={waDialogOpen} onOpenChange={(v) => { if (!sendingWa) setWaDialogOpen(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-500" /> Send WhatsApp Update
            </DialogTitle>
            <DialogDescription>
              Send a status update to <strong>{order?.customer_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-2.5 rounded-lg bg-muted/40 text-xs">
              <p className="font-medium mb-1">Order: {order?.co_number}</p>
              <p>Status: {order?.status} | Pieces: {order?.pieces_ordered}</p>
            </div>
            {/* Recipients picker */}
            <div className="space-y-1.5">
              <Label>Recipients</Label>
              {(() => {
                const rows = [];
                const seen = new Set();
                const push = (phone, label, badge) => {
                  const norm = String(phone || '').trim();
                  if (!norm || seen.has(norm)) return;
                  seen.add(norm);
                  rows.push({ phone: norm, label, badge });
                };
                push(order?.customer_phone, 'Primary Customer', 'Primary');
                (order?.notification_phones || []).forEach(p => push(p?.phone, p?.name || 'Contact', null));
                if (rows.length === 0) {
                  return (
                    <p className="text-xs text-red-500" data-testid="wa-no-recipients">
                      No WhatsApp recipients on this order. Add a customer phone or notification contacts in Edit Order.
                    </p>
                  );
                }
                return (
                  <div className="border rounded-md divide-y max-h-48 overflow-y-auto" data-testid="wa-recipients-list">
                    {rows.map(r => {
                      const checked = waSelectedPhones.includes(r.phone);
                      return (
                        <label
                          key={r.phone}
                          className="flex items-center gap-2.5 px-2.5 py-2 cursor-pointer hover:bg-muted/50"
                          data-testid={`wa-recipient-row-${r.phone}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setWaSelectedPhones(prev => v
                                ? [...prev.filter(p => p !== r.phone), r.phone]
                                : prev.filter(p => p !== r.phone));
                            }}
                            data-testid={`wa-recipient-checkbox-${r.phone}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate flex items-center gap-1.5">
                              {r.label}
                              {r.badge && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1">{r.badge}</Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{r.phone}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
              <p className="text-[11px] text-muted-foreground">
                {waSelectedPhones.length} of {(order?.customer_phone ? 1 : 0) + (order?.notification_phones?.length || 0)} selected
              </p>
            </div>
            <div className="space-y-1">
              <Label>Message *</Label>
              <Textarea
                value={waMessage}
                onChange={e => setWaMessage(e.target.value)}
                placeholder="e.g. Your order is now in production and expected to be ready by next week."
                rows={4}
                data-testid="wa-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaDialogOpen(false)} data-testid="wa-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSendWhatsApp}
              disabled={sendingWa || waSelectedPhones.length === 0 || !waMessage.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="wa-send-btn"
            >
              {sendingWa ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send to {waSelectedPhones.length} recipient{waSelectedPhones.length === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Issuance Dialog (diamonds & chains) */}
      <Dialog open={!!editIssuance} onOpenChange={(v) => { if (!v) setEditIssuance(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-issuance-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Edit {editIssuance?.material_type === 'diamonds' ? 'Diamond' : editIssuance?.material_type === 'chains' ? 'Chain' : 'Gold'} Issuance
            </DialogTitle>
            <DialogDescription>
              Original entry: {editIssuance?.quantity}{editIssuance?.material_type === 'gold' ? 'g' : (editIssuance?.material_type === 'diamonds' ? 'ct' : ' chains')} for {editIssuance?.pieces_covered} pcs · {editIssuance?.date && formatDate(editIssuance.date)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Quantity {editIssuance?.material_type === 'gold' ? '(grams)' : (editIssuance?.material_type === 'diamonds' ? '(carats)' : '(chains)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={issuanceForm.quantity}
                onChange={(e) => setIssuanceForm({ ...issuanceForm, quantity: e.target.value })}
                data-testid="edit-issuance-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Pieces Covered</Label>
              <Input
                type="number"
                min="0"
                value={issuanceForm.pieces_covered}
                onChange={(e) => setIssuanceForm({ ...issuanceForm, pieces_covered: e.target.value })}
                data-testid="edit-issuance-pieces"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={issuanceForm.notes}
                onChange={(e) => setIssuanceForm({ ...issuanceForm, notes: e.target.value })}
                rows={2}
                data-testid="edit-issuance-notes"
              />
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded px-3 py-2">
              On save, total issued, pieces issued, balance and issued status will be auto-recomputed from all entries.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIssuance(null)} disabled={savingIssuance}>Cancel</Button>
            <Button onClick={handleSaveEditIssuance} disabled={savingIssuance} data-testid="save-issuance-edit-btn">
              {savingIssuance ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Receipt Accept/Reject Dialog */}
      <Dialog open={!!receiptAction} onOpenChange={(v) => { if (!v && !savingReceipt) setReceiptAction(null); }}>
        <DialogContent className="max-w-md" data-testid="receipt-action-dialog">
          <DialogHeader>
            <DialogTitle className={cn("font-heading flex items-center gap-2", receiptAction?.kind === 'accept' ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400")}>
              {receiptAction?.kind === 'accept' ? <><CheckCircle2 className="h-5 w-5" /> Accept Receipt</> : <><X className="h-5 w-5" /> Reject Receipt</>}
            </DialogTitle>
            <DialogDescription>
              {receiptAction?.kind === 'accept'
                ? `Confirm receipt of ${receiptAction.receipt.pieces} pcs from the factory. Once signed, pieces will be added to "Received from Factory" and you can move them forward.`
                : `Reject ${receiptAction?.receipt.pieces} pcs. Pieces will be sent back to the factory's "Finished Goods" stage so they can address the issue.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded bg-slate-50 dark:bg-neutral-900 border text-xs">
              <p><span className="text-muted-foreground">Pieces:</span> <span className="font-semibold">{receiptAction?.receipt.pieces}</span></p>
              <p><span className="text-muted-foreground">From:</span> {receiptAction?.receipt.from_substage}</p>
              <p><span className="text-muted-foreground">Dispatched by:</span> {receiptAction?.receipt.dispatched_by} on {receiptAction?.receipt.dispatched_at && formatDateTime(receiptAction.receipt.dispatched_at)}</p>
              {receiptAction?.receipt.notes && <p className="italic"><span className="text-muted-foreground">Note:</span> {receiptAction.receipt.notes}</p>}
            </div>
            <div className="space-y-2">
              <Label>Type your name to sign <span className="text-red-500">*</span></Label>
              <Input
                value={receiptForm.signature}
                onChange={(e) => setReceiptForm({ ...receiptForm, signature: e.target.value })}
                placeholder="Your full name"
                autoFocus
                data-testid="receipt-signature-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{receiptAction?.kind === 'reject' ? 'Reason for rejection' : 'Notes (optional)'}</Label>
              <Textarea
                value={receiptForm.notes}
                onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
                rows={2}
                placeholder={receiptAction?.kind === 'reject' ? 'e.g. quantity mismatch, quality issue...' : 'Any additional notes'}
                data-testid="receipt-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReceiptAction(null)} disabled={savingReceipt}>Cancel</Button>
            <Button
              onClick={handleSubmitReceipt}
              disabled={savingReceipt || !receiptForm.signature.trim()}
              className={cn("text-white", receiptAction?.kind === 'accept' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700")}
              data-testid="confirm-receipt-action-btn"
            >
              {savingReceipt ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (receiptAction?.kind === 'accept' ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <X className="h-4 w-4 mr-2" />)}
              {receiptAction?.kind === 'accept' ? 'Sign & Accept' : 'Sign & Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditDispatchDialog
        open={!!editingDispatch}
        orderId={id}
        dispatch={editingDispatch}
        onClose={() => setEditingDispatch(null)}
        onSaved={() => fetchOrder?.()}
      />

      <FactoryDeadlineDialog
        orderId={id}
        currentValue={order?.factory_deadline}
        open={factoryDeadlineOpen}
        onOpenChange={setFactoryDeadlineOpen}
        onSaved={fetchOrder}
      />
    </div>
  );
};

export default OrderDetail;
